/* ==========================================================================
   Casemira C3 - page collection, VAGUES 12 et 13                 28/08/2026
   --------------------------------------------------------------------------
   VAGUE 13 ajoute trois choses à ce fichier, écrites en §§ 6, 7 et 8 :
     6. l'APERÇU RAPIDE (feuille au téléphone, panneau centré sur ordinateur),
        rempli à partir du `<template>` que chaque carte porte - donc sans une
        seule requête ;
     7. le PONT vers `window.CasemiraVision` : chargement du widget au premier
        signal d'intention seulement, réveil du serveur à l'approche, appel de
        `open()` sur place, et repli intact sur `?visualiser=1` ;
     8. « MA PIÈCE » : lecture (jamais écriture) de la mémoire de pièce, et
        bascule des libellés et de la carte par une classe sur la racine.

   Un seul fichier de plus n'a PAS été créé, et c'est délibéré : ces trois
   comportements partagent la mécanique de feuille, le verrou de défilement et
   le voile de la vague 12. Les séparer aurait coûté deux requêtes de plus pour
   dupliquer trente lignes.
   --------------------------------------------------------------------------
   Trois comportements de la vague 12, chargés en `defer` par
   `sections/main-collection-product-grid.liquid` APRÈS
   `casemira-collection.js` :

     1. LA RANGÉE JUSTE. Le rail et la carte visualiseur sont posés sur une
        rangée EXPLICITE de la grille (`grid-row`), écrite par Liquid pour la
        densité par défaut. Ce script la recalcule pour la densité et la
        largeur RÉELLES, et surtout la ramène en arrière quand la grille est
        trop courte pour l'atteindre - sans quoi une collection de 20 tapis
        vue en six colonnes ouvrirait deux rangées vides.

     2. L'ENTRÉE DANS L'ÉCRAN. Un `IntersectionObserver`, une seule fois par
        objet : le rail prend `.is-in` (fondu + 12 px, échelonnés de 60 ms par
        le CSS), la carte visualiseur prend `.is-live` (la boucle des trois
        tapis démarre, et s'arrête quand elle ressort).

     3. LA FEUILLE « COMMENT ÇA MARCHE ». Ouverture, fermeture par le voile,
        par la croix et par Échap, focus rendu au bouton.

   TROIS PRINCIPES, hérités des vagues 1, 2 et 5 :

   · RIEN AVANT `load`. Aucun observateur n'est armé, aucune animation n'est
     autorisée tant que la page n'a pas fini de charger : le LCP de la
     collection est une photo de la première rangée, et il ne partage son
     image avec rien.

   · TOUS LES ÉCOUTEURS SONT DÉLÉGUÉS AU DOCUMENT ET POSÉS UNE FOIS. `facets.js`
     détruit et recrée `#ProductGridContainer` à chaque filtre : il n'y a rien à
     rebrancher, seulement à ré-observer (`collection:rerendered`).

   · LE CONTENU N'ATTEND JAMAIS UNE ANIMATION POUR EXISTER. Si ce fichier ne
     se charge pas, le rail et la carte sont là, à leur place, lisibles ; ils
     ne bougent simplement pas.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__cmPlusInit) return;
  window.__cmPlusInit = true;

  /* ── LES CHAINES, LUES UNE FOIS ────────────────────────────────────────
     `snippets/casemira-js-strings.liquid` pose `FoxTheme.casemira.collection`
     juste apres `js-variables`, donc avant ce module. On lit la table UNE fois
     ici, jamais a chaque rendu de carte, et chaque lecture porte son repli
     francais code en dur : une cle absente du fichier de locale ferait rendre
     « Translation missing » a `| t`, et une zone jamais remplie reste `{}`.
     Aucun ecran n'affiche `undefined`, a aucun moment d'une vague en cours.

     LES INTERPOLATIONS PASSENT PAR UN JETON entre crochets (`[n]`, `[p]`,
     `[v]`), remplace ici : c'est le motif deja tenu par
     `FoxTheme.variantStrings.unavailable_with_option`. */
  var S = (window.FoxTheme && window.FoxTheme.casemira && window.FoxTheme.casemira.collection) || {};

  function sFill(tpl, token, value) {
    return String(tpl).split(token).join(value);
  }

  /* Ces deux bandeaux sont assembles par `innerHTML` : une chaine de locale y
     est du TEXTE, jamais du balisage. Elle est donc echappee avant d'y entrer -
     un guillemet ou une esperluette dans une traduction ne peut pas casser
     l'attribut ni la balise qui la porte. */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var GRID_ID = 'ProductsList';
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function grid() {
    return document.getElementById(GRID_ID);
  }

  /* ══════════════════════════════════════════════════════════════════════
     0 bis. LA CABINE D'ESSAYAGE EST DERRIÈRE UN RÉGLAGE  VAGUE 17, 30/08/2026
     ----------------------------------------------------------------------
     Décision de Raph du 30/08 : la cabine d'essayage en collection promet
     « tape une carte, ton tapis est chez toi » et le rendu prend dix à douze
     secondes. Elle sort de la publication de C3 sans sortir du code : le jour
     où un pré-rendu existe, une case cochée la rallume entière.

     UNE SEULE LECTURE, ET ELLE EST FAITE ICI. Le Liquid pose
     `data-cm-cabine="0|1"` sur le conteneur de la section
     (`sections/main-collection-product-grid.liquid`). On le lit une fois, à
     l'initialisation, et on en fait une constante. Pas de second test dispersé
     dans les écouteurs : ce qui n'est pas armé ne peut pas se déclencher, et
     c'est la seule façon de garantir « zéro requête vers l'hôte du visualiseur
     depuis une page collection » autrement qu'en parole.

     CE QUI N'EST PAS ARMÉ QUAND ELLE EST ÉTEINTE : les écoutes de
     `casemira-vision:room-set` / `room-cleared`, le bandeau « Votre pièce est
     prête », le mode essayage et ses voiles, l'interception du clic sur les
     cartes, le pont vers `CasemiraVision` (clic sur `[data-cm-viz]`, survol qui
     réveille le serveur, chargement anticipé quand une pièce est mémorisée), le
     réveil à l'approche de la carte « Ma pièce », et la construction du
     catalogue - qui n'a plus aucun appelant.

     CE QUI NE BOUGE PAS : tout le reste du fichier. Les rangées, le rail, la
     mini-fiche, sa galerie, ses badges, sa ligne Klarna, la feuille « Comment
     ça marche ». Et `casemira-visualiseur-facade.js` comme la fiche produit ne
     sont pas touchés : la cabine s'éteint EN COLLECTION, le visualiseur de la
     fiche reste ce qu'il est.
     ══════════════════════════════════════════════════════════════════════ */
  var CABINE = (function () {
    var host = document.querySelector('[data-cm-cabine]');
    /* Le nom de l'attribut n'est écrit qu'UNE fois dans tout le fichier, et le
       contrôle v17 le compte : deux écritures, ce serait deux endroits à tenir
       d'accord le jour où il change de nom. */
    return !!host && host.dataset.cmCabine === '1';
  })();

  /* ── 1. La rangée juste ──────────────────────────────────────────────────
     On ne DEVINE pas le nombre de colonnes (il dépend du réglage de section,
     de la largeur, et de la densité choisie par le visiteur) : on le LIT dans
     `grid-template-columns`, qui est la valeur résolue par le navigateur.
     C'est la seule source qui ne peut pas mentir. */

  function columnCount(g) {
    var t = window.getComputedStyle(g).gridTemplateColumns;
    if (!t || t === 'none') return 0;
    return t.split(' ').filter(function (v) {
      return v && v !== '0px';
    }).length;
  }

  /* Les blocs pleine largeur qui vivent DANS la grille, dans l'ordre où ils
     apparaissent. `p` : après combien de rangées de produits le bloc se pose.
     `eats` : combien de rangées il consomme (la carte visualiseur partage sa
     rangée avec des cartes produit sur ordinateur, elle n'en consomme donc
     aucune ; au téléphone elle est pleine largeur et en consomme une). */
  function plan(cols) {
    var wide = cols <= 2; /* téléphone : la carte visualiseur prend la rangée */
    var univers = document.querySelector('#' + GRID_ID + ' .cmu');
    var out = [];
    if (univers) {
      out.push({ el: univers, p: cols === 1 ? 4 : 2, eats: 1, varD: null, varM: null });
    }
    var v = document.querySelector('#' + GRID_ID + ' .cmv');
    if (v) out.push({ el: v, p: wide ? 5 : 3, eats: wide ? 1 : 0, varD: '--cmv-row-d', varM: '--cmv-row-m' });
    var r = document.querySelector('#' + GRID_ID + ' .cmr');
    if (r) out.push({ el: r, p: wide ? 8 : 5, eats: 1, varD: '--cmr-row-d', varM: '--cmr-row-m' });
    return out;
  }

  function place() {
    var g = grid();
    if (!g) return;
    var cols = columnCount(g);
    if (!cols) return;

    var cards = g.querySelectorAll('.f-column.product-column').length;
    if (!cards) return;
    var rows = Math.ceil(cards / cols);

    var blocks = plan(cols);
    var eaten = 0;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      /* Le bloc « Explorer » n'est pas à nous : on ne fait que compter la
         rangée qu'il consomme, on ne la réécrit pas. */
      if (!b.varD) {
        eaten += b.eats;
        continue;
      }
      var after = b.p;
      /* La grille n'a pas assez de rangées : le bloc se pose juste après la
         dernière, plutôt que d'ouvrir des rangées vides derrière elle. */
      if (rows < after) after = rows;
      var row = after + 1 + eaten;
      b.el.style.setProperty(b.varD, String(row));
      b.el.style.setProperty(b.varM, String(row));
      eaten += b.eats;
    }
  }

  /* ── 2. L'entrée dans l'écran ────────────────────────────────────────── */

  var seen = new WeakSet();

  function observeRails() {
    if (!window.IntersectionObserver) {
      document.querySelectorAll('.cmr').forEach(function (n) {
        n.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target); /* « une seule fois », littéralement */
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    document.querySelectorAll('.cmr').forEach(function (n) {
      if (seen.has(n)) return;
      seen.add(n);
      io.observe(n);
    });
  }

  /* ── 2 bis. LA VIDÉO DE DÉMONSTRATION            VAGUE 14, 28/08/2026 ────
     La boucle CSS de la vague 12 n'existe plus (« ça fait cheap », Raph le
     28/08) : il n'y a donc plus de classe `is-live` à poser. Ce qui la remplace,
     quand les métachamps de collection sont posés, est une vraie `<video>`.

     ELLE NE COÛTE RIEN TANT QU'ELLE N'EST PAS VUE. Les deux `<source>` portent
     `data-src` et non `src` : une `<video>` sans source n'a rien à télécharger,
     quels que soient ses attributs, et `autoplay` ne peut pas la faire partir
     en avance. On pose les adresses ICI - c'est-à-dire après l'événement `load`
     (`arm()`) ET quand la carte approche de l'écran (200 px de marge).

     `<source media>` garde le choix de la source côté NAVIGATEUR : `load()`
     réévalue les media queries, donc un téléphone qu'on fait pivoter reprend la
     bonne source sans une ligne de JavaScript de plus.

     L'observateur n'est PAS « une seule fois » : la vidéo doit se mettre en
     pause quand la carte sort de l'écran, c'est tout l'intérêt.

     EN MOUVEMENT RÉDUIT, LES ADRESSES NE SONT JAMAIS POSÉES. Il reste le
     poster, qui est une image fixe de la démonstration : la carte dit la même
     chose, sans mouvement et sans un octet de vidéo. */
  var vizIo = null;

  function vizVideoLoad(v) {
    var sources = v.querySelectorAll('source[data-src]');
    if (!sources.length) return false;
    sources.forEach(function (src) {
      src.setAttribute('src', src.getAttribute('data-src'));
      src.removeAttribute('data-src');
    });
    v.load();
    return true;
  }

  function observeViz() {
    var vids = document.querySelectorAll('[data-cmv-video]');
    if (!vids.length) return;
    if (reduced) return;
    if (!window.IntersectionObserver) {
      vids.forEach(function (v) {
        vizVideoLoad(v);
      });
      return;
    }
    if (!vizIo) {
      vizIo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var v = e.target;
            if (e.isIntersecting) {
              vizVideoLoad(v);
              var pr = v.play();
              /* `play()` est refusé sur les navigateurs qui exigent un geste :
                 le poster reste, et c'est la bonne réponse. */
              if (pr && pr.catch) pr.catch(function () {});
            } else if (!v.paused) {
              v.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
    }
    vids.forEach(function (v) {
      vizIo.observe(v);
    });
  }

  /* ── 3. Les flèches du module            SUPPRIMÉES EN VAGUE 18 ────────
     Le module « Les préférés de nos clients » n'a plus de flèches. Sur
     ordinateur il n'y a plus rien à faire défiler - la vague 18 en fait une
     composition de cinq produits, pas une file - et au téléphone c'est le
     doigt qui défile, comme il l'a toujours fait.

     Ce qui sort avec elles : `railStep`, `syncArrows`, l'écouteur de clic sur
     `.cmr__arw` et l'écouteur de défilement en capture qui rafraîchissait leur
     état à chaque geste sur la bande. Un écouteur de document survit à ce que
     le Liquid ne rend plus ; il fallait donc les retirer et pas seulement
     cesser de dessiner les boutons.
     Le placement dans la grille (§ 1) et l'entrée en fondu (§ 2) ne changent
     pas : ils portent sur `.cmr`, qui est toujours la racine du module. */

  /* ── 4. La feuille « Comment ça marche » ─────────────────────────────── */

  var lastOpener = null;

  function sheet() {
    return document.getElementById('CmxSheet');
  }

  function veil() {
    return document.getElementById('CmxVeil');
  }

  /* Ceinture, exactement celle de `assets/casemira-badges.js` sur la fiche :
     `position: fixed` dépend d'un ancêtre sans `transform` ni `filter`, et la
     feuille naît dans `#ProductsList`, une grille sur laquelle on ne peut pas
     parier éternellement. On la sort sur `body` à la première ouverture, une
     fois pour toutes. */
  function detach(node) {
    if (node && node.parentNode !== document.body) document.body.appendChild(node);
  }

  function openSheet(opener) {
    var s = sheet();
    var v = veil();
    if (!s || !v) return;
    detach(v);
    detach(s);
    lastOpener = opener || null;
    v.classList.add('is-open');
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    var close = s.querySelector('.cmx-sheet__close');
    if (close) close.focus();
  }

  function closeSheet() {
    var s = sheet();
    var v = veil();
    if (!s || !v) return;
    v.classList.remove('is-open');
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
    lastOpener = null;
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-cmx-open]')) {
      event.preventDefault();
      openSheet(t.closest('[data-cmx-open]'));
      return;
    }
    if (t.closest('[data-cmx-close]') || t.id === 'CmxVeil') closeSheet();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var s = sheet();
    if (s && s.classList.contains('is-open')) closeSheet();
  });

  /* ══════════════════════════════════════════════════════════════════════
     6. LA MINI-FICHE                                    VAGUE 15, 29/08/2026
     ----------------------------------------------------------------------
     Elle REMPLACE l'aperçu rapide de la vague 13, jugé « cheap et amateur » par
     Raph le 28/08 - et il avait raison sur le point qui compte : l'aperçu
     forçait la photo en HORIZONTAL alors que presque tout le catalogue est
     vertical. Ici la photo vit dans un cadre 4:5, `object-fit: cover`, et rien
     n'est jamais couché.

     Une feuille de 92 % au téléphone, un panneau de 1040 px en deux colonnes
     sur ordinateur, remplis à partir du `<template>` que chaque carte porte.
     AUCUNE REQUÊTE n'est nécessaire pour l'ouvrir : c'est la condition posée
     par le brief (moins de 150 ms après le clic) et c'est pour cela que la
     donnée est rendue en Liquid.

     LA TABLE DES CLÉS DE LA CHARGE. Elles sont courtes parce qu'elles sont
     écrites trente-six fois par page ; ce fichier est leur SEUL lecteur, la
     table est donc ici et nulle part ailleurs.

       n  nom du produit                    u  adresse de la fiche
       i  TOUTES les photos, SANS largeur (on l'ajoute au moment de l'usage)
       k  leurs `alt`, dans le même ordre - absent quand tous sont vides
       v  la vue à plat, celle qu'on envoie au visualiseur
       x  les images de texture (`detail-texture`), pour le rendu du motif
       t  couloir / escalier / exterieur / absent
       r  [note, nombre d'avis]
       f  [[clé d'icône, libellé], …] - « Lavable en machine », « Antidérapant »
       q  [prix le plus bas, prix le plus haut], déjà formatés par la boutique
       c  nombre de largeurs, sur un tapis au mètre
       w  ces largeurs, nettoyées (« 60 », « 80 », …)
       g  la fiche écrite : f forme, c composition, e épaisseur, d description
       s  [[cote, prix, id de variante, 0 si épuisé | 1, prix barré], …]
       p  1 = les photos au-delà des trois premières ne sont pas là
       m  1 = la liste des formats n'est pas là
       y  index (0..2) de l'option de taille, écrit avec `m` seulement (19/10/2026)

     LA RANGÉE DE FORMAT N'A PAS CHANGÉ DE RÈGLE : `row[3] !== 0` veut toujours
     dire « disponible ». Trois cases suffisent au cas ordinaire, et les deux
     dernières n'apparaissent que sur un format épuisé ou en promotion.

     LES ADRESSES SONT SANS LARGEUR, et c'est ce qui tient le budget de 2,5 Ko :
     la même adresse sert la vignette (200), la grande photo (900) et la vue à
     plat envoyée au visualiseur (1200). Sur un tapis à dix photos, c'est vingt
     adresses de moins par carte.
     ══════════════════════════════════════════════════════════════════════ */

  /* ── LA DURÉE DE SORTIE EST LUE DANS LE CSS      VAGUE 19, 30/08/2026 ────
     Elle valait 460 en dur ici, pour une transition de 420 déclarée là-bas :
     deux vérités, et un `hidden` posé 40 ms trop tard ou trop tôt le jour où
     l'une des deux bouge. `--cmq-fall` est déclarée UNE fois, dans
     `casemira-collection-plus.css` (§ 6, en tête), et lue ici. La marge d'une
     image (16 ms) reste, elle, une décision de ce fichier : c'est le temps qu'il
     faut pour que la dernière image de la sortie soit peinte avant que la
     feuille quitte l'arbre. Repli sur 200 ms si la variable manque (feuille non
     chargée : rien ne s'ouvrira de toute façon). */
  var qvExitMs = 0;
  function qvExit() {
    if (qvExitMs) return qvExitMs;
    var s = qvEl();
    var raw = s ? window.getComputedStyle(s).getPropertyValue('--cmq-fall') : '';
    var n = parseFloat(raw);
    if (/ms/.test(raw)) qvExitMs = n;
    else if (/s/.test(raw)) qvExitMs = n * 1000;
    qvExitMs = (qvExitMs > 0 ? qvExitMs : 200) + 16;
    return qvExitMs;
  }

  /* Les largeurs servies. Trois valeurs, pas une de plus : la vignette de la
     colonne (60 px de large, écran à 3x), la grande photo (440 px, écran à 2x)
     et la vue envoyée au visualiseur. Elles sont ici parce que ce sont des
     décisions de rendu et non des constantes de mise en page. */
  var QV_W_THUMB = 200;
  var QV_W_SHOT = 900;

  var qvIsOpen = false;
  var qvOpener = null;
  var qvData = null;
  var qvHideTimer = 0;
  var qvPushed = false;
  /* La photo montrée, et le format choisi. `-1` veut dire « aucun », et c'est
     l'état d'ouverture : le prix est alors la fourchette. */
  var qvShot = 0;
  var qvSel = -1;

  function qvEl() {
    return document.getElementById('CmqSheet');
  }

  function qvVeilEl() {
    return document.getElementById('CmqVeil');
  }

  function withWidth(u, w) {
    if (!u) return '';
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function absUrl(u) {
    if (!u) return '';
    try {
      return new URL(u, window.location.href).href;
    } catch (e) {
      return u;
    }
  }

  /* L'adresse de la fiche peut déjà porter `?variant=` : la vague 3 y pose la
     cote filtrée. On la retire pour construire les liens de format, et on la
     garde pour « Voir la fiche complète » - le visiteur qui a filtré sur
     160 × 230 doit retrouver son format en arrivant. */
  function bareUrl(u) {
    return String(u || '').split('?')[0];
  }

  function qvRead(card) {
    var tpl = card && card.querySelector('template[data-cm-qv]');
    if (!tpl) return null;
    var raw = tpl.content ? tpl.content.textContent : tpl.textContent;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* Le formatage monétaire ne sert QUE sur le chemin de repli (plus de quatorze
     variantes, la liste vient de `/products/<handle>.js`, où les prix sont des
     centimes). Sur le chemin normal, les prix arrivent déjà formatés par
     `money_without_trailing_zeros`, c'est-à-dire par la boutique elle-même :
     aucune chance de diverger de ce que la carte affiche. */
  function qvMoney(cents) {
    var cur = 'EUR';
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        cur = window.Shopify.currency.active;
      }
    } catch (e) {
      /* boutique sans objet Shopify : l'euro est le bon défaut ici */
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 0,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return String(Math.round(cents / 100));
    }
  }

  /* ── 6.1 Le remplissage ─────────────────────────────────────────────────
     Tout est écrit par le DOM et jamais par `innerHTML` : les titres, les
     descriptions et les libellés de format viennent du catalogue, et une
     apostrophe typographique ou un `<` dans un nom de produit ne doit pas
     pouvoir ouvrir une balise. `textContent` ne peut pas se tromper. */

  /* LE BADGE N'EST PAS DANS LA CHARGE, IL EST LU SUR LA CARTE. C'est la seule
     façon de tenir la règle « même badge que la carte » sans la réécrire : la
     grille arbitre remise > Best-seller > Lavable en CSS, sur ce qui est
     RÉELLEMENT rendu (`casemira-collection-plus.css` § 1), et un second
     arbitrage en JavaScript ferait une deuxième vérité qui divergerait le jour
     où le seuil du badge de remise change.

     ON LIT DONC LE STYLE CALCULÉ et non l'ordre du DOM : dans la carte, la
     pastille « Lavable » est écrite AVANT « Best-seller », qui est écrit avant
     la remise, alors que la priorité est exactement l'inverse. Le seul badge
     dont `display` n'est pas `none` est celui que le visiteur voit. */
  function qvBadgeOf(card) {
    if (!card) return '';
    var all = card.querySelectorAll('.casemira-sale-badge, .casemira-badge');
    for (var i = 0; i < all.length; i++) {
      if (window.getComputedStyle(all[i]).display === 'none') continue;
      var t = (all[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (t) return t;
    }
    return '';
  }

  function qvBadge(card) {
    var b = qvEl().querySelector('[data-cmq-badge]');
    if (!b) return;
    var t = qvBadgeOf(card);
    b.textContent = t;
    b.hidden = !t;
  }

  /* ── La galerie ────────────────────────────────────────────────────────
     UNE SEULE MÉCANIQUE POUR LES DEUX ÉCRANS : un défilement horizontal natif,
     une cellule par photo, `scroll-snap` en `proximity`. Au téléphone le doigt
     le fait glisser ; sur ordinateur, les vignettes et la flèche lui donnent
     une position par `scrollTo`. Rien n'est animé à la main, et la position de
     lecture est celle que le navigateur tient déjà - il n'y a pas deux vérités
     à garder d'accord.

     `proximity` ET NON `mandatory`, pour la raison du rail de la vague 12 : un
     alignement obligatoire reprend la main au milieu d'un lancer et rend le
     geste caoutchouteux. */

  function qvShotsBox() {
    var s = qvEl();
    return s ? s.querySelector('[data-cmq-shots]') : null;
  }

  function qvMakeShot(u, alt, k, name) {
    var cell = document.createElement('div');
    cell.className = 'cmq__shot';
    var img = document.createElement('img');
    /* LES ATTRIBUTS DE CHARGEMENT AVANT `src`, ET PAS APRÈS. Le navigateur
       décide du sort d'une image au moment où l'adresse lui est donnée : un
       `loading` posé ensuite arrive trop tard et ne sert plus à rien.

       LA PREMIÈRE PHOTO EST PRESSÉE, LES AUTRES NON. C'est la seule qui se voit
       à l'ouverture ; elle part donc en `eager` et en priorité haute, pour être
       là dans la trame qui suit le clic. Les suivantes vivent hors du cadre :
       `lazy` les fait arriver quand la vignette ou le doigt les amène, ce qui
       est exactement le moment utile. */
    if (k === 0) {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.loading = 'lazy';
    }
    img.decoding = 'async';
    /* L'alt du catalogue quand il existe ; sinon le nom du produit sur la
       première photo seulement. Douze fois le même nom serait douze fois le
       même bruit dans un lecteur d'écran. */
    img.alt = alt || (k === 0 ? name || '' : '');
    img.src = withWidth(u, QV_W_SHOT);
    cell.appendChild(img);
    return cell;
  }

  function qvMakeThumb(u, k) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cmq__thumb';
    b.setAttribute('data-cmq-shot', String(k));
    b.setAttribute('aria-label', sFill(S.photo || 'Photo [n]', '[n]', k + 1));
    var img = document.createElement('img');
    /* LES VIGNETTES NE SONT PAS PARESSEUSES, ET LA VAGUE 15 L'A APPRIS SUR LA
       PAGE RÉELLE. Relevé le 29/08 sur `Isfahan N°09` : trois cases de la
       colonne restaient BLANCHES, en désordre, pendant que leurs voisines
       s'affichaient.

       LA CAUSE. `loading="lazy"` se décide sur l'intersection avec le
       VIEWPORT ; or ces vignettes vivent dans une colonne qui a son propre
       `overflow-y`, à l'intérieur d'un panneau qui était encore en
       `visibility: hidden` au moment où l'adresse leur a été donnée. Le
       navigateur a donc tranché « pas encore visible », et rien ne l'a fait
       revenir dessus : la colonne ne défile que si le visiteur la fait
       défiler, et une vignette qu'on ne voit pas est une vignette sur laquelle
       on ne clique pas. Le paresseux devenait un blocage.

       CE QU'ELLES COÛTENT. 200 px de large, une dizaine au plus, demandées
       seulement à l'ouverture de la mini-fiche. `fetchpriority="low"` les met
       derrière la grande photo, qui est la seule que le visiteur regarde dans
       la trame qui suit le clic : elles arrivent après elle, mais elles
       arrivent TOUTES. C'est le bon arbitrage pour une bande de vignettes -
       leur seul travail est de dire ce qu'il y a à voir. */
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'low');
    img.decoding = 'async';
    img.alt = '';
    img.src = withWidth(u, QV_W_THUMB);
    b.appendChild(img);
    return b;
  }

  /* ── LES POINTS DE LA FICHE            ★ VAGUE 16, point 4c ────────────
     Même objet, même mécanique : une pastille à largeur CONSTANTE qui montre au
     plus sept points, une fenêtre qui glisse pour garder le point actif au
     milieu, un point actif allongé, et les points de bout réduits quand il en
     reste derrière. Les valeurs et le nom des classes sont ceux de
     `casemira-pdp-reskin.js` (`galleryDots`), parce que c'est le même repère et
     qu'il n'y a aucune raison qu'il se lise autrement d'une surface à l'autre.

     LA PASTILLE NE CHANGE PAS DE LARGEUR PENDANT LE GLISSEMENT : la fenêtre
     contient toujours exactement un point actif, donc toujours la même somme de
     largeurs. C'est ce qui permet d'animer la largeur des points sans jamais
     remettre en page quoi que ce soit hors de la pastille - qui est elle-même
     en `position: absolute` au-dessus de la photo. */
  var QV_DOTS_WINDOW = 7;

  function qvDotsTrack() {
    var s = qvEl();
    var box = s ? s.querySelector('[data-cmq-dots]') : null;
    if (!box) return null;
    var track = box.querySelector('.cm-gallery-dots__track');
    if (!track) {
      track = document.createElement('div');
      track.className = 'cm-gallery-dots__track';
      box.appendChild(track);
    }
    return track;
  }

  function qvDotsBuild(n) {
    var track = qvDotsTrack();
    if (!track) return;
    if (track.childElementCount === n) return;
    track.textContent = '';
    for (var i = 0; i < n; i++) {
      var dot = document.createElement('span');
      dot.className = 'cm-gallery-dots__dot';
      track.appendChild(dot);
    }
  }

  function qvDotsMark(total, idx) {
    var s = qvEl();
    var box = s ? s.querySelector('[data-cmq-dots]') : null;
    var track = qvDotsTrack();
    if (!box || !track) return;
    var offset = 0;
    if (total > QV_DOTS_WINDOW) {
      offset = Math.min(Math.max(idx - 3, 0), total - QV_DOTS_WINDOW);
    }
    box.style.setProperty('--cm-dot-offset', offset);
    /* Moins de sept photos : la pastille se resserre plutôt que de laisser du
       vide à droite de la dernière. */
    box.style.setProperty('--cm-dot-n', Math.min(total, QV_DOTS_WINDOW));
    var dots = track.children;
    for (var j = 0; j < dots.length; j++) {
      var cls = 'cm-gallery-dots__dot';
      if (j === idx) cls += ' cm-gallery-dots__dot--active';
      if (
        (j === offset && offset > 0) ||
        (j === offset + QV_DOTS_WINDOW - 1 && offset + QV_DOTS_WINDOW < total)
      ) {
        cls += ' cm-gallery-dots__dot--edge';
      }
      if (dots[j].className !== cls) dots[j].className = cls;
    }
  }

  /* ── L'IMAGE D'OUVERTURE : LA VUE À PLAT ────────── VAGUE 20, 30/08/2026 ──
     Décision de Fable, une règle unique pour toutes les fiches : la mini-fiche
     s'ouvre sur la VUE À PLAT du tapis, et non sur la photo de la carte.

     POURQUOI. La carte vient de montrer la mise en situation - c'est elle qu'on
     a touchée. L'aperçu doit apporter TOUT DE SUITE ce que la carte ne montrait
     pas : le tapis lui-même, son motif d'un bord à l'autre, sa bordure, ses
     franges, sa forme réelle. C'est la deuxième question de tout le monde, et
     c'est déjà la raison pour laquelle `card-product.liquid` place cette photo
     en deuxième position de la galerie depuis la vague 15. Le geste suivant
     (balayage au doigt, vignette sur ordinateur) ramène la mise en situation,
     qui est juste derrière.

     COMMENT ON LA RECONNAÎT, ET CE QUE LE CATALOGUE EN DIT. Par le nom de
     fichier : `…-vue-complete…`. Relevé du 30/08 sur les 940 produits du
     catalogue (`products.json`, recoupé avec les deux sitemaps produits) :
     940 sur 940 en portent une, soit 100 %, et elle est à l'indice 1 dans 939
     cas. Le seul écart est `kit-tapis-de-bain-caramel-harlem`, où elle est à
     l'indice 2 - c'est justement pour lui qu'on cherche le NOM plutôt que de
     compter jusqu'à deux. Deux réserves relevées et tenues ici : le suffixe se
     décline en `-vue-complete` (323 produits) ET `-vue-complete-new` (617),
     donc on cherche une SOUS-CHAÎNE ; et 27 produits en ont plusieurs, donc on
     prend la première.

     LE REPLI EST LA PREMIÈRE IMAGE, et c'est le comportement d'avant la vague.

     ON NE DÉPLACE PAS LE DÉFILEMENT, ON CHANGE L'ORDRE. Régler `scrollLeft` sur
     une cellule exigerait de LIRE la largeur du cadre au moment même où la
     galerie vient d'être insérée : c'est la reprise de mise en page forcée que
     la vague 19 a retirée du chemin d'ouverture. La vue à plat passe donc en
     tête de la liste affichée, `scrollLeft` reste à 0, et les points comme les
     vignettes disent 1 sur n sans qu'on ait rien à leur expliquer.

     LA CHARGE DE LA CARTE N'EST PAS TOUCHÉE : `d.i` garde son ordre, parce que
     `d.i[0]` est aussi la vignette envoyée au visualiseur (§ 7). Seul l'ordre
     AFFICHÉ change, et `qvAddShots` continue d'ajouter à la fin des deux.

     RÉVERSIBLE PAR UNE CONSTANTE : `QV_OPEN_FLAT = false` et la mini-fiche
     rouvre sur la photo de la carte, sans qu'une autre ligne bouge. */
  var QV_OPEN_FLAT = true;
  var QV_FLAT_TAG = 'vue-complete';

  function qvFlatIndex(list) {
    for (var i = 0; i < list.length; i++) {
      if (qvFileName(list[i]).indexOf(QV_FLAT_TAG) >= 0) return i;
    }
    return 0;
  }

  function qvOrdered(d) {
    var list = (d.i || []).slice();
    var alts = (d.k || []).slice();
    if (!QV_OPEN_FLAT || list.length < 2) return { list: list, alts: alts };
    var f = qvFlatIndex(list);
    if (f > 0) {
      list.unshift(list.splice(f, 1)[0]);
      if (alts.length > f) alts.unshift(alts.splice(f, 1)[0]);
    }
    return { list: list, alts: alts };
  }

  /* ── LA POSITION DE LECTURE ─────────────────── VAGUE 20 BIS, 30/08/2026 ──
     LA GALERIE REVIENT À SA PREMIÈRE CELLULE, ET LA COLONNE DE VIGNETTES À SON
     PREMIER RANG. Une seule fonction, appelée à DEUX moments qui ne sont pas
     interchangeables (voir plus bas), parce que le bogue vient précisément de
     là.

     CE QUI ÉTAIT ÉCRIT, ET POURQUOI ÇA NE POUVAIT PAS MARCHER. `qvGallery`
     posait `box.scrollLeft = 0` juste après avoir reconstruit la liste. Or
     `qvGallery` est appelée par `qvFill`, c'est-à-dire AVANT `s.hidden = false`
     : à cet instant la feuille est encore `display: none`, la boîte défilante
     n'a AUCUNE boîte de rendu, et écrire `scrollLeft` sur un élément sans boîte
     ne fait rien du tout (« if the element does not have any associated box,
     terminate these steps »). Les deux moteurs, eux, MÉMORISENT le décalage de
     défilement d'un élément qu'on masque et qu'on remontre : à la réouverture,
     la galerie reprenait la position de la fiche PRÉCÉDENTE. Mesuré sur le
     preview réel, Chromium et WebKit, même chiffre : ouvrir, balayer jusqu'à la
     3e photo, fermer, rouvrir une autre carte rendait `scrollLeft` 780 (390 px)
     ou 880 (1440 px) au lieu de 0, la 3e photo du NOUVEAU produit à l'écran, et
     des points qui disaient « 1 sur n » pendant ce temps.

     Le cas qui marchait disait déjà où était la faute : rouvrir AVANT la fin du
     fondu de sortie (la feuille est alors encore dans l'arbre) donnait 0. Ce
     n'est pas la remise à zéro qui manquait, c'est la boîte.

     RIEN N'EST MESURÉ ICI. `scrollLeft = 0` et `scrollTop = 0` sont des
     écritures ; `qvMark` ne lit une géométrie que si `qvShot > 0` (garde de la
     vague 19), et il vaut 0 juste après. Le chemin d'ouverture ne gagne donc
     aucune reprise de mise en page forcée, ce qui était la condition de la
     vague 19.

     ET AUCUN DÉFILEMENT DOUX NE PEUT S'Y GLISSER : `scroll-behavior` n'est pas
     une propriété héritée, `.cmq__shots` ne la déclare pas, et le contrôle le
     vérifie sur le preview plutôt que de le supposer. */
  /* ── VAGUE 21, 30/08/2026 · ET LA COLONNE VERTICALE AVEC ─────────────────
     Retour de Raph le 30/08 à 14 h 50, site publié, iPhone : « j'ouvre un
     aperçu, je fais défiler vers le bas, je ferme, j'en ouvre un autre : il
     s'ouvre là où l'autre a été fermé ».

     C'est le MÊME défaut que la vague 20 bis, sur une TROISIÈME boîte. La
     vague 20 bis avait rendu à zéro les deux boîtes qu'elle regardait - la
     galerie horizontale (`[data-cmq-shots]`) et la colonne de vignettes
     (`[data-cmq-thumbs]`) - et laissé la plus grande de toutes : celle qui
     porte le TEXTE. Elle change de nom selon la largeur, et c'est
     précisément pourquoi les deux étaient nécessaires :

       · au téléphone, `[data-cmq-scroll]` est la boîte défilante (elle porte
         `overflow-y: auto`, § 6 de la feuille de style) ;
       · à partir de 1000 px, `.cmq__scroll` passe en `display: contents` et
         s'efface de la mise en page : c'est `.cmq__info` qui défile.

     Les deux sont remises à zéro sans condition. Écrire `scrollTop = 0` sur
     une boîte qui ne défile pas ne fait rien et ne coûte rien ; demander
     laquelle des deux défile coûterait une lecture de géométrie, et le chemin
     d'ouverture n'en gagne aucune (condition de la vague 19).

     ET C'EST BIEN ICI QUE ÇA SE PASSE, pas dans `qvFill` : `qvHome` est
     appelée une ligne après `void s.offsetWidth`, c'est-à-dire une fois la
     feuille rentrée dans l'arbre. Une boîte en `display: none` n'a aucune
     boîte de rendu, et `scrollTop` écrit sur un élément sans boîte ne fait
     rien - c'est le diagnostic complet de la vague 20 bis, et il vaut mot
     pour mot pour cette troisième boîte. */
  function qvHome() {
    var s = qvEl();
    if (!s) return;
    var box = s.querySelector('[data-cmq-shots]');
    var th = s.querySelector('[data-cmq-thumbs]');
    var col = s.querySelector('[data-cmq-scroll]');
    var info = s.querySelector('.cmq__info');
    if (box) box.scrollLeft = 0;
    if (th) th.scrollTop = 0;
    if (col) col.scrollTop = 0;
    if (info) info.scrollTop = 0;
    qvShot = 0;
    qvMark();
  }

  function qvGallery(d) {
    var s = qvEl();
    var box = s.querySelector('[data-cmq-shots]');
    var th = s.querySelector('[data-cmq-thumbs]');
    box.textContent = '';
    th.textContent = '';
    var ord = qvOrdered(d);
    var list = ord.list;
    var alts = ord.alts;
    var name = d.n || '';
    list.forEach(function (u, k) {
      box.appendChild(qvMakeShot(u, alts[k], k, name));
      th.appendChild(qvMakeThumb(u, k));
    });
    qvDotsBuild(list.length);
    /* Une seule photo : ni vignettes, ni points, ni flèches. Un point solitaire
       et une colonne d'une vignette ressemblent à une erreur de rendu. */
    var dots = s.querySelector('[data-cmq-dots]');
    if (dots) dots.classList.toggle('is-many', list.length > 1);
    th.hidden = list.length < 2;
    qvHome();
    qvFetchShots(d);
  }

  /* Les vignettes, les points et les deux flèches disent tous la même chose :
     où l'on est. Ils sont donc mis à jour au même endroit, et jamais ailleurs. */
  function qvMark() {
    var s = qvEl();
    if (!s) return;
    var box = s.querySelector('[data-cmq-shots]');
    var th = s.querySelector('[data-cmq-thumbs]');
    var n = box ? box.children.length : 0;
    if (qvShot < 0) qvShot = 0;
    if (qvShot > n - 1) qvShot = n > 0 ? n - 1 : 0;
    var i;
    for (i = 0; i < th.children.length; i++) {
      th.children[i].classList.toggle('is-on', i === qvShot);
      th.children[i].setAttribute('aria-current', i === qvShot ? 'true' : 'false');
    }
    qvDotsMark(n, qvShot);
    var prev = s.querySelector('.cmq__arw--p');
    var next = s.querySelector('.cmq__arw--n');
    if (prev) prev.hidden = n < 2 || qvShot <= 0;
    if (next) next.hidden = n < 2 || qvShot >= n - 1;
    /* La vignette active reste dans le cadre, et c'est la COLONNE qui défile,
       jamais la page : `scrollIntoView` remonterait la collection entière
       derrière le voile. On calcule donc le décalage nous-mêmes.

       VAGUE 19 · ON NE MESURE PAS QUAND IL N'Y A RIEN À FAIRE DÉFILER. À
       l'ouverture, `qvShot` vaut TOUJOURS 0 : la première vignette est déjà en
       haut de la colonne, aucun décalage n'est possible. Lire `clientHeight`,
       `offsetTop` et `offsetHeight` à ce moment-là forçait pourtant une reprise
       de mise en page complète, juste après avoir inséré toute la galerie -
       c'est-à-dire au pire endroit. La lecture ne se fait donc plus que quand le
       visiteur a changé de photo, ce qui est le seul cas où elle sert. */
    var on = qvShot > 0 && th.children[qvShot];
    if (on && th.clientHeight) {
      var top = on.offsetTop;
      var bottom = top + on.offsetHeight;
      if (top < th.scrollTop) th.scrollTop = top;
      else if (bottom > th.scrollTop + th.clientHeight) th.scrollTop = bottom - th.clientHeight;
    }
  }

  function qvGo(i, smooth) {
    var box = qvShotsBox();
    if (!box || !box.children.length) return;
    var n = box.children.length;
    if (i < 0) i = 0;
    if (i > n - 1) i = n - 1;
    qvShot = i;
    var x = i * box.clientWidth;
    if (box.scrollTo) box.scrollTo({ left: x, behavior: smooth ? 'smooth' : 'auto' });
    else box.scrollLeft = x;
    qvMark();
  }

  /* Les repères suivent le défilement, ils ne le pilotent pas. Le calcul est un
     arrondi de la position sur la largeur d'une cellule : aucune mesure de
     chaque photo, donc rien qui force une reprise de mise en page pendant le
     geste. */
  function qvSyncGallery() {
    var box = qvShotsBox();
    if (!box || !box.clientWidth) return;
    var i = Math.round(box.scrollLeft / box.clientWidth);
    if (i === qvShot) return;
    qvShot = i;
    qvMark();
  }

  /* ── Les photos qui manquent ───────────────────────────────────────────
     `p:1` veut dire que le garde-fou de poids de la carte a laissé sortir les
     photos au-delà des trois premières. On va les chercher pendant que la
     mini-fiche est DÉJÀ OUVERTE sur les trois qu'elle a : le visiteur ne voit
     pas la requête, seulement des vignettes qui s'ajoutent sous celles qui sont
     là. C'est le seul chemin de photos qui touche au réseau, et il ne s'ouvre
     que sur les produits que le garde-fou a mis hors budget.

     LES PICTOGRAMMES DE MARQUE SONT ÉCARTÉS ICI COMME EN LIQUID, par le même
     préfixe de nom de fichier : deux lecteurs de la même règle, écrite deux
     fois parce qu'aucun des deux ne peut lire l'autre - et rappelée dans les
     deux commentaires pour qu'on les change ensemble. */
  var qvShotAsked = {};

  function qvFileName(u) {
    return String(u || '').split('?')[0].split('/').pop().toLowerCase();
  }

  function qvFetchShots(d) {
    if (!d || !d.p) return;
    var handle = bareUrl(d.u).split('/products/')[1];
    if (!handle || !window.fetch) return;
    if (qvShotAsked[handle]) {
      qvAddShots(d, qvShotAsked[handle]);
      return;
    }
    /* 09/10/2026 · L'ADRESSE DE LA CARTE, PRÉFIXE DE LANGUE COMPRIS. Hors du
       français, le handle est traduit (/en/products/sand-beige-...) et
       `/products/<handle traduit>.js` sans racine de langue ne trouve rien :
       l'aperçu restait sur ses trois premières photos. On garde donc la
       racine de langue de l'adresse de la carte (tout ce qui précède
       `/collections/` ou `/products/`) : « /en/products/<handle>.js ». */
    var root = bareUrl(d.u).split('/collections/')[0].split('/products/')[0];
    window
      .fetch(root + '/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (p) {
        /* 09/10/2026 · LES PLANCHES PAR LANGUE. `p.images` ne porte pas les
           alt : on lit `p.media`, qui les porte, et on écarte les images
           marquées « [xx] » pour une autre langue que celle de la page (même
           règle que card-product.liquid et la fiche). Le marqueur figure dans
           l'alt de chaque langue, peu importe donc que cette requête ne soit pas
           préfixée par la racine de langue. Sans `media`, repli sur `p.images`. */
        var moi = '[' + String(document.documentElement.lang || 'fr').toLowerCase().split('-')[0] + ']';
        var src = p.media && p.media.length
          ? p.media
              .filter(function (m) {
                if (!m || m.media_type !== 'image' || !m.src) return false;
                var t = String(m.alt || '').slice(0, 4).toLowerCase();
                return !(/^\[[a-z]{2}\]$/.test(t) && t !== moi);
              })
              .map(function (m) {
                return m.src;
              })
          : p.images || [];
        var out = src.filter(function (u) {
          return qvFileName(u).indexOf('casemira-') !== 0;
        });
        qvShotAsked[handle] = out;
        qvAddShots(d, out);
      })
      .catch(function () {
        /* Sans les photos, la galerie garde les trois qu'elle a, et c'est un
           état complet - pas un trou. */
      });
  }

  function qvAddShots(d, urls) {
    if (qvData !== d) return; /* le visiteur a changé de tapis entre-temps */
    var s = qvEl();
    if (!s) return;
    var box = s.querySelector('[data-cmq-shots]');
    var th = s.querySelector('[data-cmq-thumbs]');
    var dots = s.querySelector('[data-cmq-dots]');
    var seen = {};
    (d.i || []).forEach(function (u) {
      seen[qvFileName(u)] = 1;
    });
    var added = 0;
    urls.forEach(function (u) {
      var key = qvFileName(u);
      if (!key || seen[key]) return;
      seen[key] = 1;
      var k = d.i.length;
      d.i.push(u);
      box.appendChild(qvMakeShot(u, '', k, d.n || ''));
      th.appendChild(qvMakeThumb(u, k));
      added++;
    });
    d.p = 0;
    if (!added) return;
    qvDotsBuild(d.i.length);
    dots.classList.toggle('is-many', d.i.length > 1);
    th.hidden = d.i.length < 2;
    qvMark();
  }

  /* ── Le nom, le modèle, la note ───────────────────────────────────────── */

  /* LE MODÈLE PASSE EN ITALIQUE, comme sur la fiche (`.cm-model`). Il est ce
     qui suit le DERNIER « - » du titre : c'est une règle de nommage du
     catalogue (« Tapis Lavable Shaggy Écru - Sahel »), pas une donnée de plus à
     écrire trente-six fois par page. Un titre sans tiret reste entier. */
  function qvName(d) {
    var h = qvEl().querySelector('#CmqName');
    h.textContent = '';
    var t = String(d.n || '');
    var cut = t.lastIndexOf(' - ');
    if (cut < 0) {
      h.textContent = t;
      return;
    }
    h.appendChild(document.createTextNode(t.slice(0, cut + 3)));
    var em = document.createElement('i');
    em.className = 'cmq__model';
    em.textContent = t.slice(cut + 3);
    h.appendChild(em);
  }

  function qvRate(d) {
    var s = qvEl();
    var p = s.querySelector('[data-cmq-rate]');
    if (!(d.r && d.r[0] > 0 && d.r[1] > 0)) {
      p.hidden = true;
      return;
    }
    var rate = Math.round(Number(d.r[0]) * 10) / 10;
    var count = Number(d.r[1]);
    var on = p.querySelector('[data-cmq-stars]');
    /* La largeur de la couche dorée EST la note. Une note de 4,3 montre 86 %
       d'or, pas cinq étoiles pleines : la maquette montrait cinq pleines, ce qui
       aurait fait dire à la mini-fiche ce que la fiche ne dit pas. */
    if (on) on.style.width = Math.max(0, Math.min(100, (rate / 5) * 100)) + '%';
    /* RETROFIT i18n (R11) : « 4,7 » en francais, « 4.7 » en anglais. Le pont porte
       la MEME cle que `card-product.liquid`, qui peint la note de la carte. */
    var sepNote = (S && S.separateurDecimal) || ',';
    p.querySelector('[data-cmq-note]').textContent = String(rate).replace('.', sepNote);
    var avisTpl = count > 1 ? S.avisN || '[n] avis' : S.avisUn || '[n] avis';
    p.querySelector('[data-cmq-avis]').textContent = sFill(avisTpl, '[n]', count);
    p.hidden = false;
  }

  /* ── Le texte de la fiche écrite, décodé une fois ──────────────────────
     DÉFAUT RELEVÉ LE 29/08 SUR LA PAGE RÉELLE : la description affichait
     « d&#39;exception » au lieu de « d'exception ».

     POURQUOI. La description vient d'un métachamp de texte riche, rendu par
     `metafield_tag` puis nettoyé par `strip_html`. `strip_html` retire les
     BALISES, pas les ENTITÉS : l'apostrophe reste écrite `&#39;` dans la
     charge. Le `| escape` du `<template>` la protège une seconde fois
     (`&amp;#39;`), l'analyseur HTML en défait exactement une couche, et
     `JSON.parse` reçoit la chaîne littérale `d&#39;exception`. `textContent`
     l'affiche telle quelle, ce qui est son travail.

     OÙ LE CORRIGER, ET POURQUOI PAS EN LIQUID. Une chaîne de `replace` en
     Liquid ne couvrirait que les cinq entités de l'échappeur de Shopify, et le
     texte riche en produit d'autres (`&nbsp;`, `&#8217;`, `&eacute;`…). Il
     faudrait de plus l'écrire une troisième fois dans la cale de contrôle. Ce
     décodeur est donc écrit UNE fois, ici, et les TROIS lecteurs de `g.d` y
     passent : la description affichée, `rugInfo.description` envoyé au
     visualiseur, et la même dans chaque entrée du catalogue. Une seule règle,
     trois usages - et il vaut aussi pour une page encore servie depuis le
     cache de Shopify avec l'ancienne charge.

     IL N'OUVRE AUCUNE PORTE. `DOMParser` construit un document SANS contexte
     de navigation : rien n'y est exécuté, aucune ressource n'y est chargée, et
     on n'en lit que `textContent`. Le résultat repart vers `textContent` ou
     vers une charge JSON, jamais vers `innerHTML`. Décoder `&lt;script&gt;`
     rend donc la chaîne « <script> », affichée comme du texte - ce qui est
     précisément ce qu'on veut lire. */
  var qvParser = null;

  function qvText(value) {
    var t = String(value == null ? '' : value);
    if (t.indexOf('&') === -1) return t;
    try {
      if (!qvParser) qvParser = new window.DOMParser();
      var doc = qvParser.parseFromString('<!doctype html><body>' + t, 'text/html');
      return (doc && doc.body && doc.body.textContent) || t;
    } catch (e) {
      /* Pas de `DOMParser` : le texte échappé reste lisible, seulement moins
         joli. On n'affiche jamais rien de moins que ce qu'on avait. */
      return t;
    }
  }

  function qvDesc(d) {
    var p = qvEl().querySelector('[data-cmq-desc]');
    var t = qvText(d.g && d.g.d);
    p.textContent = t;
    p.hidden = !t;
  }

  /* ── La ligne de faits ─────────────────────────────────────────────────
     Trois faits au plus, chacun avec son pictogramme. La charge ne porte qu'une
     LETTRE par icône ; les chemins SVG vivent dans le sprite de la coquille,
     écrits une fois pour la page entière. Une clé inconnue rend le fait sans
     pictogramme plutôt que de le taire : le mot compte plus que l'image. */
  var QV_ICONS = { w: 1, g: 1, h: 1, m: 1 };
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function qvFacts(d) {
    var box = qvEl().querySelector('[data-cmq-facts]');
    box.textContent = '';
    var list = d.f;
    /* La vague 13 écrivait `f` en CHAÎNE (« Laine · Lavable »). Une page servie
       depuis le cache de Shopify pendant le déploiement peut encore en porter
       une : on la refuse plutôt que d'en lire les lettres une par une. */
    if (!list || typeof list.slice !== 'function' || typeof list === 'string') {
      box.hidden = true;
      return;
    }
    list.slice(0, 3).forEach(function (f) {
      var key = String((f && f[0]) || '');
      var label = String((f && f[1]) || '');
      if (!label) return;
      var cell = document.createElement('span');
      cell.className = 'cmq__fact';
      if (QV_ICONS[key]) {
        var svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        var use = document.createElementNS(SVG_NS, 'use');
        use.setAttribute('href', '#cmq-i-' + key);
        svg.appendChild(use);
        cell.appendChild(svg);
      }
      var t = document.createElement('span');
      t.textContent = label;
      cell.appendChild(t);
      box.appendChild(cell);
    });
    box.hidden = !box.children.length;
  }

  /* ── Le prix ───────────────────────────────────────────────────────────
     FOURCHETTE TANT QUE RIEN N'EST CHOISI, prix du format ensuite. C'est la
     décision de Raph sur le doute n° 1 des maquettes, et la raison est écrite
     dans la coquille : annoncer « 49 € » sur un tapis dont le format de salon
     coûte 134 € est une promesse que le visiteur découvre rompue une page plus
     loin.

     LES PRIX SONT DÉJÀ FORMATÉS PAR LA BOUTIQUE (`money_without_trailing_zeros`
     en Liquid, `qvMoney` sur le seul chemin de repli) : ce fichier ne calcule
     jamais un prix, il en choisit un. */
  function qvCount(d) {
    var n = (d.s || []).length;
    if (!n) return '';
    var tpl = n > 1 ? S.formatN || '[n] formats' : S.formatUn || '[n] format';
    return sFill(tpl, '[n]', n);
  }

  function qvPrice(d) {
    var box = qvEl().querySelector('[data-cmq-price]');
    box.textContent = '';
    var big = document.createElement('b');
    var note = document.createElement('em');
    var row = qvSel >= 0 && d.s ? d.s[qvSel] : null;

    if (row) {
      big.textContent = row[1];
      box.appendChild(big);
      if (row[4]) {
        var was = document.createElement('s');
        was.textContent = row[4];
        box.appendChild(was);
      }
      note.textContent = sFill(S.format || 'Format : [v] cm', '[v]', row[0]);
      box.appendChild(note);
      return;
    }

    var q = d.q || [];
    /* 09/10/2026 : un berbère sur mesure (`z`) a, comme le tapis au mètre, un prix
       de départ (sa plus petite taille standard) et un prix selon ses dimensions. */
    if (d.c || d.z) {
      big.textContent = q[0] ? sFill(S.desPrix || 'dès [p]', '[p]', q[0]) : '';
      note.textContent = S.selonLargeurLongueur || 'selon la largeur et la longueur';
    } else if (q[0] && q[1] && q[0] !== q[1]) {
      big.textContent = q[0] + ' – ' + q[1];
      note.textContent = qvCount(d);
    } else {
      big.textContent = q[0] || q[1] || '';
      note.textContent = qvCount(d);
    }
    if (big.textContent) box.appendChild(big);
    if (note.textContent) box.appendChild(note);
  }

  /* ── Les formats ───────────────────────────────────────────────────────
     Les pastilles de la fiche, à la lettre : 52 px, prix sous la taille (Option
     A de la fiche, portée ici le 19/10/2026), rayon de la fiche, choisie =
     bordure encre et fond sable, épuisée = pointillé et texte barré.

     UN FORMAT ÉPUISÉ EST UN `<span>` ET NON UN BOUTON DÉSACTIVÉ : il n'y a donc
     rien à intercepter, rien à annuler, et le clavier ne s'y arrête pas. C'est
     la règle du tiroir de formats de la fiche, portée telle quelle.

     UN FORMAT DISPONIBLE EST UN `<button>` ET NON UN LIEN, et c'est le
     changement de la vague 15 : toucher un format ne quitte plus la page, il
     règle le PRIX et l'adresse de « Voir la fiche complète ». Le visiteur
     compare ses six formats sans jamais perdre la grille - c'était tout l'objet
     d'une mini-fiche. */
  function qvSizes(d) {
    var s = qvEl();
    var box = s.querySelector('[data-cmq-sizes]');
    box.textContent = '';
    var rows = d.s || [];
    rows.forEach(function (row, k) {
      var live = row[3] !== 0;
      var el = document.createElement(live ? 'button' : 'span');
      el.className = 'cmq__s' + (live ? '' : ' is-oos') + (k === qvSel ? ' is-on' : '');
      if (live) {
        el.type = 'button';
        el.setAttribute('data-cmq-size', String(k));
        el.setAttribute('aria-pressed', k === qvSel ? 'true' : 'false');
      } else {
        el.setAttribute('aria-disabled', 'true');
      }
      var b = document.createElement('b');
      b.textContent = row[0];
      var p = document.createElement('span');
      p.textContent = row[1];
      el.appendChild(b);
      el.appendChild(p);
      if (!live) {
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = S.epuise || 'épuisé';
        el.appendChild(sr);
      }
      box.appendChild(el);
    });
    /* Au-delà de neuf formats, la grille de trois rangées ne les montre plus
       tous : un fondu en bas dit qu'elle continue, exactement comme le tiroir de
       la fiche (`.dw-picker__scroll--fade`). */
    box.classList.toggle('is-long', rows.length > 9);
  }

  function qvLabel(d, text) {
    var lab = qvEl().querySelector('[data-cmq-lab]');
    lab.textContent = text || '';
    lab.hidden = !text;
  }

  function qvMoreLink(d) {
    var a = qvEl().querySelector('[data-cmq-more]');
    if (!a) return;
    var row = qvSel >= 0 && d.s ? d.s[qvSel] : null;
    /* Sans format choisi, l'adresse est celle de la carte - qui porte déjà
       `?variant=` quand le visiteur a filtré sur une cote (vague 3). On ne lui
       reprend pas ce qu'il a demandé pour ne rien lui donner à la place. */
    a.href = row ? bareUrl(d.u) + '?variant=' + row[2] : d.u || '#';
  }

  function qvSelect(k) {
    var d = qvData;
    if (!d || !d.s || !d.s[k] || d.s[k][3] === 0) return;
    qvSel = k;
    qvSizes(d);
    qvPrice(d);
    /* ★ VAGUE 16 : le prix change, la mensualité change. Elles ne peuvent pas
       dire deux choses différentes du même format. */
    qvKlarna(d);
    qvMoreLink(d);
  }

  /* ── Le tapis au mètre ─────────────────────────────────────────────────
     168 combinaisons ne sont pas un choix, ce sont des données : on choisit une
     LARGEUR, puis on tape une longueur, et cela se fait dans le configurateur
     de la fiche. La règle de la vague 12, portée telle quelle.

     CE QUE LA VAGUE 15 AJOUTE : les largeurs RÉELLES. « 7 largeurs » ne dit pas
     si celle du couloir du visiteur existe, et c'est la seule question qu'il se
     pose devant un tapis au mètre. */
  function qvConf(d) {
    var s = qvEl();
    var conf = s.querySelector('[data-cmq-conf]');
    var sub = s.querySelector('.cmq__conf-s');
    if (sub) sub.hidden = false;
    var confTpl =
      d.c > 1
        ? S.surMesureLargeurN || 'Sur mesure : [n] largeurs, longueur au choix'
        : S.surMesureLargeurUn || 'Sur mesure : [n] largeur, longueur au choix';
    s.querySelector('[data-cmq-conf-t]').textContent = sFill(confTpl, '[n]', d.c);
    var w = s.querySelector('[data-cmq-widths]');
    var list = d.w || [];
    w.textContent = list.length ? list.join(' · ') + ' cm' : '';
    w.hidden = !list.length;
    conf.hidden = false;
  }

  /* ── Le berbère sur mesure (09/10/2026) ────────────────────────────────
     Ses variantes sont cinq tailles standard et deux cents paliers de prix
     (« Sur mesure jusqu'à X m² ») : aucune grille de formats. Une phrase renvoie
     au configurateur de la fiche, où l'on saisit ses dimensions au centimètre.
     Même bloc que le tapis au mètre, sans la ligne des largeurs. 14h30 (décision
     Raphaël 13h52) : la phrase ne liste plus les tailles standard, `z` n'est
     plus qu'un drapeau. */
  function qvBerbere(d) {
    var s = qvEl();
    var conf = s.querySelector('[data-cmq-conf]');
    s.querySelector('[data-cmq-conf-t]').textContent =
      S.taillesStandard || 'Choisissez vos dimensions sur la fiche';
    /* Une seule phrase : le sous-titre du tapis au mètre (« Choisissez la largeur
       ci-dessous… ») ne vaut pas ici, il n'y a pas de largeurs dessous. */
    var sub = s.querySelector('.cmq__conf-s');
    if (sub) sub.hidden = true;
    var w = s.querySelector('[data-cmq-widths]');
    w.textContent = '';
    w.hidden = true;
    conf.hidden = false;
  }

  /* Plus de quatorze variantes : la liste n'était pas dans la charge, on la
     demande. La mini-fiche est déjà ouverte pendant ce temps : le nom, les
     photos, la note et les boutons sont là, seule la grille de formats arrive
     après. */
  var qvAsked = {};

  function qvFetchSizes(d, done) {
    if (d.s || d.c || d.z) {
      done();
      return;
    }
    var handle = bareUrl(d.u).split('/products/')[1];
    if (!handle || !window.fetch) {
      done();
      return;
    }
    if (qvAsked[handle]) {
      d.s = qvAsked[handle];
      done();
      return;
    }
    /* 19/10/2026 · LA RACINE DE LANGUE, comme `qvFetchShots` (règle du 06/09).
       Hors du français, le handle de la carte est traduit et
       `/products/<handle traduit>.js` sans racine ne trouve rien : la grille
       restait vide. */
    var root = bareUrl(d.u).split('/collections/')[0].split('/products/')[0];
    window
      .fetch(root + '/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (p) {
        /* 19/10/2026 · L'INDEX DE LA TAILLE VIENT DE LA CARTE (`y`), résolu en
           Liquid par `casemira-options-map` dans les 21 langues : la réponse
           porte les noms d'option dans la langue de la page (« Größe »), que
           le test sur le nom français ne reconnaît pas. Le test reste en repli
           pour une charge sans `y`. */
        var oi = typeof d.y === 'number' ? d.y : -1;
        if (oi === -1) {
          (p.options || []).forEach(function (o, k) {
            var name = String(o && o.name ? o.name : o).toLowerCase();
            if (oi === -1 && (name.indexOf('taille') > -1 || name.indexOf('dimension') > -1)) oi = k;
          });
        }
        var seen = {};
        var out = [];
        (p.variants || []).forEach(function (v) {
          var raw = oi >= 0 && v.options && v.options[oi] ? v.options[oi] : v.title;
          raw = String(raw || '').trim();
          var key = raw.toLowerCase().replace(/×/g, 'x').replace(/cm/g, '').replace(/\s/g, '');
          if (!key || seen[key]) return;
          seen[key] = 1;
          var label = raw.replace(/cm/g, '').trim().replace(/\s*[xX]\s*/g, ' × ');
          var row = [label, qvMoney(v.price), v.id, v.available ? 1 : 0];
          if (v.compare_at_price && v.compare_at_price > v.price) {
            row.push(qvMoney(v.compare_at_price));
          }
          out.push(row);
        });
        qvAsked[handle] = out;
        d.s = out;
        done();
      })
      .catch(function () {
        done();
      });
  }

  function qvFormats(d) {
    var s = qvEl();
    var box = s.querySelector('[data-cmq-sizes]');
    var conf = s.querySelector('[data-cmq-conf]');
    var wait = s.querySelector('[data-cmq-wait]');

    if (d.c) {
      s.classList.add('is-conf');
      box.textContent = '';
      wait.hidden = true;
      qvConf(d);
      qvLabel(d, S.surMesure || 'Sur mesure');
      return;
    }

    /* 09/10/2026 14h30 : `is-brb` laisse la ligne de faits du berbère passer à
       deux lignes au téléphone (délai plus long, `casemira-collection-plus.css`). */
    s.classList.toggle('is-brb', !!d.z);

    if (d.z) {
      s.classList.add('is-conf');
      box.textContent = '';
      wait.hidden = true;
      qvBerbere(d);
      qvLabel(d, S.surMesure || 'Sur mesure');
      return;
    }

    s.classList.remove('is-conf');
    conf.hidden = true;

    if (d.s) {
      wait.hidden = true;
      qvSizes(d);
      qvLabel(d, (S.dimensions || 'Dimensions') + ' · ' + qvCount(d));
      return;
    }

    /* Le drapeau `m` : la liste est ailleurs. On dit qu'on la cherche plutôt que
       de laisser un trou - et si la requête échoue, la ligne disparaît et la
       fiche complète reste à un bouton de distance. */
    qvSizes({ s: [] });
    qvLabel(d, S.dimensions || 'Dimensions');
    wait.hidden = false;
    qvFetchSizes(d, function () {
      if (qvData !== d) return; /* le visiteur a changé de tapis entre-temps */
      wait.hidden = true;
      qvSizes(d);
      qvLabel(d, (S.dimensions || 'Dimensions') + ' · ' + qvCount(d));
      qvPrice(d);
      qvKlarna(d);
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
     6.3 LES BADGES DE LA FICHE                          VAGUE 16, 29/08/2026
     ----------------------------------------------------------------------
     Trois ou quatre badges dérivés des métachamps de spécification, LES MÊMES
     que la fiche produit, par les mêmes treize règles. La charge de la carte
     n'en porte que la clé et le libellé (`b:[["main","Fait main"],…]`, rendus
     par `casemira-badges.liquid` en mode `json`) ; les quatorze dessins vivent
     une fois par page dans le sprite de la coquille, et on pose un `<use>`.

     LE BALISAGE EST CELUI DE LA FICHE À LA CLASSE PRÈS, parce que c'est
     `casemira-badges.css` - la vraie feuille, chargée par la section de la
     grille - qui l'habille. Aucune règle de badge n'est réécrite ici.

     SANS `data-cm-tip` : pas de panneau explicatif, et c'est un choix écrit
     dans `casemira-badges.liquid`. Le badge reste alors exactement ce qu'il
     est, une icône et un mot - la dégradation que `casemira-badges.js`
     documente lui-même, et qui est au pixel celle de la fiche puisque le
     bouton qu'il n'ajoute pas « n'apporte aucune boîte ». */
  function qvBadges(d) {
    var s = qvEl();
    var ul = s.querySelector('[data-cmq-badges]');
    if (!ul) return;
    ul.textContent = '';
    var rows = d.b || [];
    if (!rows.length) {
      ul.hidden = true;
      ul.removeAttribute('data-count');
      return;
    }
    var n = Math.min(rows.length, 4);
    ul.setAttribute('data-count', String(n));
    for (var i = 0; i < n; i++) {
      var key = String(rows[i][0] || '');
      var li = document.createElement('li');
      li.className = 'cm-badges__item';
      li.setAttribute('data-cm-key', key);
      var svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('class', 'cm-badges__icon');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      var use = document.createElementNS(SVG_NS, 'use');
      /* `href` ET `xlink:href` : Safari ne lit encore que le second sur un
         `<use>` construit en JavaScript. Deux attributs, un seul dessin. */
      use.setAttribute('href', '#cmb-i-' + key);
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#cmb-i-' + key);
      svg.appendChild(use);
      var lab = document.createElement('span');
      lab.className = 'cm-badges__label';
      lab.textContent = String(rows[i][1] || '');
      li.appendChild(svg);
      li.appendChild(lab);
      ul.appendChild(li);
    }
    ul.hidden = false;
  }

  /* ══════════════════════════════════════════════════════════════════════
     6.4 LA LIGNE KLARNA DE LA FICHE                     VAGUE 16, 29/08/2026
     ----------------------------------------------------------------------
     « ou 26,34 € × 3 sans frais », la pilule blanche au badge rose, sous le
     prix. Même balisage, même habillage, même phrase que la fiche.

     LE CALCUL EST CELUI DE LA FICHE, LIGNE POUR LIGNE. Le bloc « contenu
     Liquid » des gabarits (`templates/product.*.json`) fait trois choses :
     bornes 35 € à 1 500 €, mensualité = prix arrondi au centime SUPÉRIEUR
     divisé par trois, format « 26,34 € » sans séparateur de milliers. Les
     trois sont reprises ici telles quelles. Ce n'est pas une seconde source de
     vérité, c'est la même formule appliquée au même prix : recalculer
     autrement serait la seule façon de se contredire.

     LE PRIX, LUI, VIENT DE LA BOUTIQUE. La charge ne porte que des prix DÉJÀ
     FORMATÉS par `money_without_trailing_zeros` : on les relit en centimes avec
     la convention française (le point sépare les milliers, la virgule les
     centimes), et c'est le seul endroit du fichier qui le fasse. Les adresses
     d'images ne contiennent jamais de virgule, les prix jamais d'espace fine
     insécable ailleurs qu'entre le nombre et le symbole : la règle est étroite
     et elle est écrite ici.

     SANS FORMAT CHOISI, LA MENSUALITÉ EST CELLE DU PREMIER PRIX, et la ligne le
     dit : « ou dès 16,34 € × 3 ». C'est la même honnêteté que la fourchette au-
     dessus d'elle - annoncer la mensualité du grand format sur un tapis dont on
     montre le prix d'entrée serait la promesse que le visiteur découvre rompue
     une page plus loin. Dès qu'un format est touché, le « dès » tombe et le
     montant devient exact. */
  var KL_MIN = 3500;
  var KL_MAX = 150000;

  function qvCents(txt) {
    var t = String(txt || '').replace(/[^0-9.,]/g, '');
    if (!t) return 0;
    /* La virgule sépare les centimes, le point les milliers. Un montant sans
       virgule est un montant entier : « 1.029 » vaut 1 029 et non 1,029. */
    var cut = t.lastIndexOf(',');
    var whole = (cut < 0 ? t : t.slice(0, cut)).replace(/[.,]/g, '');
    var cents = cut < 0 ? '' : t.slice(cut + 1).replace(/[^0-9]/g, '');
    cents = (cents + '00').slice(0, 2);
    var n = parseInt(whole, 10);
    if (isNaN(n)) return 0;
    return n * 100 + parseInt(cents, 10);
  }

  /* RETROFIT i18n (R3) · LE MONTANT KLARNA PASSE PAR `qvMoney`. La virgule
     décimale et le « € » collé derrière étaient écrits en dur : ils sont justes
     en français et faux partout ailleurs. `qvMoney` formate déjà avec la langue
     du document et la devise active de la boutique, et il applique la même règle
     de décimales (rien quand il n'y a rien). La seule différence est le
     séparateur de milliers, que `Intl` pose : Klarna plafonne à 1 500 € et le
     montant affiché est le tiers de celui du tapis, il ne peut pas atteindre
     mille. */
  function qvKlMoney(cents) {
    return qvMoney(cents);
  }

  function qvKlarna(d) {
    var s = qvEl();
    var box = s.querySelector('[data-cmq-klarna]');
    if (!box) return;
    var strong = box.querySelector('[data-cmq-klarna-x]');
    var row = qvSel >= 0 && d.s ? d.s[qvSel] : null;
    var price = row ? row[1] : (d.q || [])[0];
    var cents = qvCents(price);
    if (!cents || cents < KL_MIN || cents > KL_MAX) {
      box.hidden = true;
      return;
    }
    /* « dès » tant que le prix montré est une AMPLITUDE : une fourchette de
       formats, ou un tapis au mètre dont le prix dépend de deux mesures. */
    var vague = !row && (!!d.c || !!d.z || !!(d.q && d.q[0] && d.q[1] && d.q[0] !== d.q[1]));
    if (strong) strong.textContent = qvKlMoney(Math.ceil(cents / 3)) + ' × 3';
    var lead = box.querySelector('.casemira-klarna-text');
    if (lead && lead.firstChild) {
      lead.firstChild.nodeValue = vague ? S.klarnaOuDes || 'ou dès ' : S.klarnaOu || 'ou ';
    }
    box.hidden = false;
  }

  /* ── La réassurance (09/10/2026 14h30) ─────────────────────────────────
     Deux versions écrites par la coquille : « Livraison offerte dès 100 € »
     et, quand la carte porte le drapeau `o` (aucune variante sous le seuil),
     « Livraison offerte ». Sans la seconde (coquille ancienne), rien ne change. */
  function qvFranco(d) {
    var s = qvEl();
    var seuil = s.querySelector('[data-cmq-franco]');
    var libre = s.querySelector('[data-cmq-franco-o]');
    if (!seuil || !libre) return;
    seuil.hidden = !!d.o;
    libre.hidden = !d.o;
  }

  function qvFill(d, card) {
    var s = qvEl();
    if (!s) return;
    qvSel = -1;
    qvGallery(d);
    qvBadge(card);
    qvName(d);
    qvRate(d);
    qvFacts(d);
    qvDesc(d);
    qvFormats(d);
    qvPrice(d);
    qvKlarna(d);
    qvBadges(d);
    qvMoreLink(d);
    qvFranco(d);
  }

  /* ── 6.2 Ouvrir, fermer, piéger le focus ──────────────────────────────── */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* « VISIBLE » VEUT DIRE DEUX CHOSES, ET LA PREMIÈRE NE SUFFIT PAS. Un élément
     en `visibility: hidden` garde sa GÉOMÉTRIE : `offsetWidth` est non nul, et
     un test qui s'arrête là le croit visible. Il n'est pourtant pas focusable -
     `focus()` part sans erreur et ne fait rien, ce qui laisse le focus là où il
     était sans que personne ne s'en aperçoive. C'est exactement le cas du lien
     « Aperçu » du bandeau de survol, replié dès que le pointeur s'en va. On lit
     donc aussi le style calculé. */
  function qvSeen(n) {
    if (!n) return false;
    if (!n.offsetWidth && !n.offsetHeight) return false;
    return window.getComputedStyle(n).visibility !== 'hidden';
  }

  function qvFocusables() {
    var s = qvEl();
    if (!s) return [];
    return [].slice.call(s.querySelectorAll(FOCUSABLE)).filter(qvSeen);
  }

  /* Verrou de défilement : `overflow` sur la RACINE, jamais `position: fixed`
     sur `body`, qui perdrait la position de lecture. Copie conforme du verrou
     de la feuille de filtres et du lecteur vidéo.

     ── VAGUE 19 · LA BARRE DE DÉFILEMENT SE MESURE UNE FOIS PAR LARGEUR ────
     `window.innerWidth - documentElement.clientWidth` sont deux lectures de
     géométrie, et elles tombaient JUSTE APRÈS l'insertion de toute la galerie
     dans la feuille : le navigateur devait donc recalculer la mise en page du
     document entier pour répondre, au pire moment de l'ouverture. Relevé au
     profileur Chrome, processeur bridé × 4 : `get innerWidth` pesait 10 % des
     échantillons de l'ouverture, plus que le remplissage de la galerie
     lui-même.

     Cette largeur ne dépend que de la fenêtre et du réglage du système. On la
     mesure donc une fois, et on la remesure quand la fenêtre change de taille -
     jamais pendant une ouverture. */
  var qvSbw = -1;
  window.addEventListener('resize', function () { qvSbw = -1; }, { passive: true });

  function qvLock() {
    if (qvSbw < 0) qvSbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--cmq-sbw', qvSbw + 'px');
    document.documentElement.classList.add('cmq-locked');
    /* ★ VAGUE 16, point 4c · LA PASTILLE DE CHAT PASSE DERRIÈRE LA FEUILLE.
       `cm-over` est le nom générique de « une surcouche plein écran est
       ouverte » : la règle qui efface le lanceur du chat, son teaser et son
       aperçu vit dans `casemira-tokens.css`, chargé sur toutes les pages, et
       elle sert le lecteur vidéo comme la mini-fiche. On pose la classe, on
       n'écrit pas une quatrième fois la règle. */
    document.documentElement.classList.add('cm-over');
  }

  /* ON NE RETIRE QUE CE QU'ON A POSÉ, ET SEULEMENT SI PLUS PERSONNE NE LA
     TIENT. `cm-over` est partagée par cinq surcouches. La mini-fiche la
     retirait en aveugle à la fermeture : si une autre surcouche vivait encore -
     le tiroir panier ouvert depuis le bouton d'ajout de la mini-fiche, le
     tiroir de navigation, la recherche, le guide des tailles - le bandeau de
     consentement et la pastille de chat se rallumaient PAR-DESSUS elle. C'est
     la faute déjà corrigée dans `casemira-c6-loupe.js` (vague 4, § « ON NE
     RETIRE PAS cm-over SI QUELQU'UN D'AUTRE LA REVENDIQUE »),
     `casemira-c6-tiroir.js` (vague R1) et `casemira-guide-tiroir.js` (C5,
     05/09) ; même forme ici.

     Les revendicateurs sont NOMMÉS un par un, et pas devinés par un sélecteur
     large : la leçon de la vague 4 est qu'un sélecteur trop large
     (`.drawer[open]`) laisse `cm-over` posée pour toujours, et qu'un bandeau
     de consentement qu'on oublie de RALLUMER est un défaut plus grave que
     l'inverse. On ne nomme donc que ce qui pose réellement `cm-over` :
       · `cmk-open`, le tiroir panier (`casemira-cart.js`) ;
       · `cmg-lock`, le guide des tailles des fiches couloir (`casemira-guide-tiroir.js`) ;
       · `search-open` / `search-is-open`, la recherche prédictive du thème ;
       · `menu-drawer[open]`, le tiroir de navigation.
     `cmq-locked` est notre propre marque : elle n'a rien à faire ici, et elle
     part de toute façon AVANT l'interrogation, juste en dessous. Le lecteur
     vidéo passe par `csm-video-open`, qui a sa propre paire de règles dans les
     tokens : il n'est pas concerné. */
  function qvQuelquUnDAutreLaTient() {
    return (
      document.documentElement.classList.contains('cmk-open') ||
      document.documentElement.classList.contains('cmg-lock') ||
      /* `cmt-lock`, le guide des tailles des fiches HORS couloir (`casemira-guide-tailles.js`, ajout R5, 05/09) ; */
      document.documentElement.classList.contains('cmt-lock') ||
      document.body.classList.contains('search-open') ||
      document.body.classList.contains('search-is-open') ||
      !!document.querySelector('menu-drawer[open]')
    );
  }

  function qvUnlock() {
    /* `cmq-locked` part D'ABORD : c'est la marque par laquelle les autres
       arbitres nous reconnaissent, et l'interroger en la portant encore ferait
       de la mini-fiche son propre revendicateur. */
    document.documentElement.classList.remove('cmq-locked');
    if (!qvQuelquUnDAutreLaTient()) document.documentElement.classList.remove('cm-over');
    document.documentElement.style.removeProperty('--cmq-sbw');
  }

  function qvPhone() {
    return window.matchMedia && window.matchMedia('(max-width: 999.98px)').matches;
  }

  /* Le bouton retour du téléphone. Sans cela, le premier réflexe d'un visiteur
     pris dans une feuille plein écran (retour Android, glissement depuis le
     bord sur iOS) le fait QUITTER la collection. Mécanique du lecteur vidéo de
     la fiche : on empile un état à l'ouverture, `popstate` devient une
     fermeture de plus, et une fermeture par un autre geste dépile. */
  function qvPush() {
    if (qvPushed || !qvPhone()) return;
    try {
      window.history.pushState({ cmqSheet: true }, '');
      qvPushed = true;
    } catch (e) {
      qvPushed = false;
    }
  }

  /* ── VAGUE 19 · LE RETOUR QU'ON A DEMANDÉ N'EST PAS CELUI DU VISITEUR ────
     `history.back()` est ASYNCHRONE : l'événement `popstate` arrive une image
     ou deux plus tard. Entre les deux, un visiteur qui rouvre aussitôt une
     mini-fiche (la croix, puis le « + » de la carte d'à côté) faisait empiler
     un nouvel état par `qvPush()` - et le `popstate` en retard, qui appartenait
     à la fermeture PRÉCÉDENTE, trouvait `qvPushed` de nouveau vrai et refermait
     la feuille qui venait de s'ouvrir. Une feuille qui se referme toute seule,
     et rien à l'écran pour l'expliquer.

     On compte donc les retours qu'on a nous-mêmes demandés. Le premier
     `popstate` qui arrive après un `qvPop()` est le nôtre : il se consomme et
     ne ferme rien. Les autres sont le geste du visiteur (bouton retour Android,
     glissement depuis le bord sur iOS) et ferment la feuille, ce qui est
     exactement le service rendu. */
  var qvPopping = 0;

  function qvPop() {
    if (!qvPushed) return;
    qvPushed = false;
    qvPopping++;
    try {
      window.history.back();
    } catch (e) {
      qvPopping--;
    }
  }

  window.addEventListener('popstate', function () {
    if (qvPopping > 0) {
      qvPopping--;
      return;
    }
    if (!qvPushed) return;
    qvPushed = false;
    if (qvIsOpen) qvClose(true);
  });

  /* La carte qui a ouvert la feuille. Elle sert à une seule chose : reconnaître
     un SECOND appui sur le même ouvreur (VAGUE 19). */
  var qvCard = null;

  function qvOpen(card, opener) {
    var s = qvEl();
    var v = qvVeilEl();
    if (!s || !v || !card) return false;
    /* ── VAGUE 19 · LE DOUBLE APPUI NE REJOUE RIEN ─────────────────────────
       Un second tap sur le même « + » (le doigt qui rebondit, le double-clic de
       la souris) refaisait tout le remplissage - galerie, formats, prix - et
       renvoyait le focus sur la croix alors que le visiteur venait peut-être de
       le poser ailleurs dans la feuille. La feuille est déjà ouverte SUR CETTE
       CARTE : il n'y a rien à faire, et ne rien faire est la bonne réponse. Sur
       une AUTRE carte, en revanche, l'appel doit passer : c'est un changement de
       produit, pas un rebond. */
    if (qvIsOpen && card === qvCard) return true;
    var d = qvRead(card);
    /* Pas de charge : on ne fabrique rien et on ne bloque rien. Le titre de la
       carte reste un lien vers la fiche, qui est la vérité complète. */
    if (!d) return false;

    qvData = d;
    qvCard = card;
    /* La carte est passée au remplissage, et pour une seule raison : le badge.
       Il n'est pas dans la charge, il est LU sur la carte, parce que c'est la
       seule façon de tenir « le même badge que la carte » sans réécrire son
       arbitrage (§ 6.1, `qvBadgeOf`). */
    qvFill(d, card);
    qvOpener = opener || null;
    window.clearTimeout(qvHideTimer);
    s.hidden = false;
    s.setAttribute('aria-hidden', 'false');
    v.classList.add('is-open');
    qvLock();
    qvPush();
    /* ── VAGUE 19 · UNE REPRISE DE STYLE, PLUS DEUX IMAGES D'ATTENTE ────────
       Il fallait bien que le navigateur PRENNE l'état de départ avant que
       `is-open` arrive, sinon les deux états se résolvent dans la même passe et
       il n'y a pas de transition du tout. La vague 15 s'en assurait par deux
       `requestAnimationFrame` imbriqués. C'est vrai, et c'est cher : DEUX
       IMAGES avant même que la transition commence.

       Lire une géométrie force le même travail, et l'oblige TOUT DE SUITE : le
       navigateur doit purger ses styles en attente pour répondre à
       `offsetWidth`. L'état de départ est donc pris dans la tâche du clic, et la
       transition part à l'image suivante au lieu de la troisième. Une reprise de
       mise en page à l'ouverture, exactement comme avant - c'est la MÊME lecture
       qu'il fallait déjà faire pour le focus, elle a seulement changé de place.

       Mesuré sur le preview réel, téléphone 390, médiane de cinq passages : la
       première peinture de la feuille tombe de 83 à 53 ms, et la feuille est
       posée en 203 ms au lieu de 366 (`perf/minifiche-{avant,apres}-x1.json`).
       Les deux tiers de cet écart viennent d'ici, le reste de la durée de
       transition. */
    void s.offsetWidth;
    /* ── VAGUE 20 BIS · ET C'EST ICI QUE LA GALERIE REVIENT À SA PREMIÈRE
       CELLULE, PAS DANS `qvFill`. La feuille vient de rentrer dans l'arbre :
       la boîte défilante a enfin une boîte de rendu, et `scrollLeft` a enfin
       un effet. Un cran plus haut, l'écriture partait dans le vide et la
       galerie rouvrait sur la photo où la fiche précédente s'était arrêtée.
       La lecture de `s.offsetWidth` juste au-dessus a déjà purgé les styles en
       attente : cette remise à zéro ne coûte pas une passe de plus. */
    qvHome();
    s.classList.add('is-open');
    /* LE FOCUS ENTRE ICI, ET PAS UNE LIGNE PLUS HAUT. Au repos le panneau est en
       `visibility: hidden`, et un élément invisible NE PEUT PAS recevoir le
       focus - l'appel part sans erreur et ne fait rien, ce qui est le pire des
       deux mondes. `focus()` déclenche lui-même la mise à jour du style qui rend
       la visibilité, et `is-open` est déjà posée : l'appel arrive donc sur un
       élément visible. */
    var close = s.querySelector('.cmq__close');
    if (close) close.focus({ preventScroll: true });
    qvIsOpen = true;
    return true;
  }

  function qvClose(fromHistory) {
    if (!qvIsOpen) return;
    var s = qvEl();
    var v = qvVeilEl();
    qvIsOpen = false;
    qvCard = null;
    if (v) v.classList.remove('is-open');
    if (s) {
      s.classList.remove('is-open');
      s.setAttribute('aria-hidden', 'true');
      /* VAGUE 20 · une feuille qui part emporte le geste avec elle. `qdRest`
         seul retirait le décalage mais laissait l'état du glissement ARMÉ : un
         doigt encore posé continuait d'écrire `--cmq-dy` sur une feuille en
         train de sortir, et le relâchement rappelait `qvClose` sur du vide. */
      qdAbort();
      qdRest(s);
    }
    qvUnlock();
    if (!fromHistory) qvPop();
    /* Le panneau ne quitte l'arbre qu'une fois la sortie terminée : un retrait
       immédiat couperait le fondu net. */
    window.clearTimeout(qvHideTimer);
    qvHideTimer = window.setTimeout(function () {
      if (qvIsOpen || !s) return;
      s.hidden = true;
    }, qvExit());
    /* LE FOCUS REVIENT, MAIS PAS N'IMPORTE OÙ. Sur ordinateur, l'ouvreur est le
       lien « Aperçu » du bandeau de survol : il vit dans une boîte que le CSS
       replie dès que le pointeur s'en va. Lui rendre le focus, ce serait le
       poser sur un élément que personne ne voit, et le visiteur au clavier
       repartirait du haut de la page. On mesure donc si l'ouvreur est encore
       VISIBLE ; sinon, le focus va au titre de la carte, qui est l'ancre stable
       de cette carte-là dans l'ordre de tabulation. */
    if (qvOpener && document.contains(qvOpener)) {
      var back = qvOpener;
      if (!qvSeen(back)) {
        var card = qvOpener.closest('.cm-card');
        back = (card && card.querySelector('.cm-card__t')) || null;
      }
      if (back) back.focus({ preventScroll: true });
    }
    qvOpener = null;
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    var opener = t.closest('[data-cm-qv-open]');
    if (opener) {
      event.preventDefault();
      qvOpen(opener.closest('.cm-card'), opener);
      return;
    }
    if (qvIsOpen && (t.closest('[data-cmq-close]') || t.id === 'CmqVeil')) {
      event.preventDefault();
      qvClose();
      return;
    }
    if (!qvIsOpen) return;

    /* Une vignette, puis une flèche. Les deux ne font qu'appeler `qvGo` : la
       galerie n'a qu'un chemin, et il passe par le défilement natif. */
    var thumb = t.closest('[data-cmq-shot]');
    if (thumb) {
      event.preventDefault();
      qvGo(parseInt(thumb.getAttribute('data-cmq-shot'), 10) || 0, true);
      return;
    }
    var step = t.closest('[data-cmq-step]');
    if (step) {
      event.preventDefault();
      qvGo(qvShot + (parseInt(step.getAttribute('data-cmq-step'), 10) || 0), true);
      return;
    }

    /* UN FORMAT NE QUITTE PLUS LA PAGE (vague 15) : il règle le prix affiché et
       l'adresse de « Voir la fiche complète ». C'est tout l'objet d'une
       mini-fiche - comparer six formats sans perdre la grille. */
    var size = t.closest('[data-cmq-size]');
    if (size) {
      event.preventDefault();
      qvSelect(parseInt(size.getAttribute('data-cmq-size'), 10) || 0);
      return;
    }

    /* Le bouton de la fiche ferme la mini-fiche avant de partir : sans cela, le
       verrou de défilement resterait posé sur la page qu'on quitte, et un retour
       arrière depuis la fiche retrouverait une collection bloquée. */
    if (t.closest('[data-cmq-more]')) qvClose();
  });

  document.addEventListener('keydown', function (event) {
    if (!qvIsOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      qvClose();
      return;
    }
    if (event.key !== 'Tab') return;
    /* LE PIÈGE À FOCUS. Il est écrit plutôt que délégué à `inert` ou à
       `<dialog>` : le thème n'utilise ni l'un ni l'autre, et un dialogue natif
       poserait son propre voile par-dessus le nôtre. */
    var list = qvFocusables();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var here = document.activeElement;
    if (event.shiftKey && (here === first || !qvEl().contains(here))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && here === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener(
    'scroll',
    function (event) {
      var t = event.target;
      if (!t || !t.classList || !t.classList.contains('cmq__shots')) return;
      qvSyncGallery();
    },
    true
  );

  /* ── 6.3 Le glissement de la feuille ──────────────────────────────────────
     Les seuils sont ceux de la feuille de filtres et du lecteur vidéo : 6 px de
     tolérance de tap, 80 px ou un geste vif pour fermer.

     ── VAGUE 20, 30/08/2026 · LE GESTE EST ÉCRIT POUR LE DOIGT, PAS POUR LA
        SOURIS ────────────────────────────────────────────────────────────────
     Retour de Raph le 30/08 à 12 h 32, sur iPhone : « quand tu veux vite balayer
     vers le bas pour fermer, ça fait un zoom bug bizarre, ça devient compliqué
     de ressortir ». Les chronos de la vague 19 avaient été pris en Chromium avec
     `page.mouse` : ils ne pouvaient pas voir ce défaut, parce qu'une souris
     n'émet ni `touchmove`, ni geste de zoom, ni défilement natif concurrent.

     TROIS CHOSES MANQUAIENT, ET CHACUNE A SA LIGNE ICI.

     1. RIEN N'EMPÊCHAIT SAFARI DE FAIRE SON PROPRE GESTE EN MÊME TEMPS. Le code
        n'écoutait que des `pointermove`. Or `preventDefault()` sur un
        `pointermove` n'annule AUCUN défilement : sur WebKit, la seule chose qui
        arrête le défilement natif et le rebond élastique est un `touchmove` NON
        PASSIF sur lequel on appelle `preventDefault()`, ou une propriété
        `touch-action` qui interdit le geste dès son départ. La feuille faisait
        donc deux mouvements à la fois : le nôtre (`transform`) et celui de
        Safari (défilement de `.cmq__scroll`, puis rebond de la page), avec en
        prime le `pointercancel` que WebKit envoie dès qu'il prend la main.
        La feuille sautait, revenait, et le doigt ne commandait plus rien.
        On écoute donc `touchstart`/`touchmove`/`touchend` en plus, et le
        `touchmove` est déclaré `{ passive: false }` : c'est la seule façon
        d'avoir le droit de dire non.

     2. UN BALAYAGE VIF ÉTAIT MESURÉ SUR TOUT LE GESTE. `dt` valait
        `event.timeStamp - qd.t0`, c'est-à-dire la durée COMPLÈTE depuis le
        poser du doigt : un geste qui hésite un tiers de seconde puis part d'un
        coup rendait une vitesse MOYENNE très inférieure au seuil de 0,5 px/ms,
        et ne fermait pas. C'est exactement le geste que Raph décrit (« vite
        balayer vers le bas »). La vitesse se lit maintenant sur les 90
        dernières millisecondes du geste, ce qui est la définition d'un lancer.

     3. UN `pointercancel` POUVAIT FERMER LA FEUILLE. La fin de geste était la
        MÊME fonction pour `pointerup` et `pointercancel`, et lisait
        `event.clientY`. Un `pointercancel` de WebKit (celui qu'il envoie quand
        il décide de faire défiler la galerie, ou quand un second doigt arrive)
        porte des coordonnées qui ne sont pas celles d'un relâchement : la
        feuille pouvait se fermer sur un geste que le visiteur n'a pas terminé.
        Une annulation ne ferme plus jamais ; elle repose la feuille, et c'est
        tout.

     CE QUI N'A PAS CHANGÉ : les seuils (80 px OU un geste vif, 6 px de tap,
     élastique de 56 px vers le haut), le fait que le geste soit un `transform`
     et rien d'autre, et le fait que RIEN NE SOIT LU pendant le mouvement.

     LA GALERIE GARDE SON GESTE HORIZONTAL. Un balayage qui commence sur les
     photos et part clairement de côté (|dx| > |dy|) n'est pas le nôtre : on
     rend la main, et le défilement à `scroll-snap` de `.cmq__shots` fait son
     travail comme avant. C'est la seule exception à « depuis n'importe où sur
     la feuille ». */

  var QD_SLOP = 6;
  var QD_DISTANCE = 80;
  var QD_VELOCITY = 0.5;
  var QD_RUBBER = 56;
  /* VAGUE 21 · la résistance du geste vers le HAUT. Elle valait 1/2 ; à 0,15 la
     feuille ne monte plus sous l'encoche (voir `qdOffset`). */
  var QD_UP = 0.15;
  /* La fenêtre sur laquelle se lit un lancer. 90 ms est la durée d'un geste vif
     à lui seul : au-delà, ce n'est plus un lancer, c'est un déplacement. */
  var QD_WINDOW = 90;

  var qd = { on: false, armed: false, id: null, x0: 0, y0: 0, t0: 0, shots: false };

  /* La traînée : deux points suffisent, le plus ancien qui soit encore dans la
     fenêtre et le dernier. Aucun tableau qui grandit, aucune lecture de
     géométrie. */
  var qdTrail = [];

  function qdSample(y, t) {
    qdTrail.push({ y: y, t: t });
    /* On jette tout ce qui est plus vieux que la fenêtre, mais jamais au point
       de n'avoir plus qu'un point : deux points sont le minimum pour qu'une
       vitesse existe. */
    while (qdTrail.length > 2 && t - qdTrail[0].t > QD_WINDOW) qdTrail.shift();
  }

  function qdSpeed(y, t) {
    var a = qdTrail.length ? qdTrail[0] : { y: qd.y0, t: qd.t0 };
    return (y - a.y) / Math.max(1, t - a.t);
  }

  /* ── VAGUE 21, 30/08/2026 · VERS LE HAUT, LA FEUILLE NE MONTE PLUS ───────
     Retour de Raph le 30/08 à 14 h 50 : « le pouce posé au niveau de l'encoche
     ne balaye pas la feuille, ça la remonte ».

     L'élastique valait `dy / 2`, plafonné à 56 px : un geste de 112 px vers le
     haut faisait monter la feuille de 56 px pleins, ce qui, sur une feuille
     déjà haute de 92 % de l'écran, la pousse SOUS l'encoche - la croix et la
     poignée partent avec. Ce n'était pas une indication, c'était un
     déplacement.

     Le facteur passe à 0,15 : 112 px de doigt donnent 17 px de feuille, et il
     faudrait 373 px pour atteindre le plafond, c'est-à-dire plus que l'écran.
     La feuille est donc bornée à zéro EN PRATIQUE, elle frémit juste assez
     pour dire qu'elle a compris le geste, et le retour est immédiat (le retour
     élastique EST le retrait de `--cmq-dy`, voir `qdFinish`). */
  function qdOffset(dy) {
    if (dy >= 0) return dy;
    return Math.max(-QD_RUBBER, dy * QD_UP);
  }

  /* ── VAGUE 19 · LE GESTE N'ÉCRIT QUE SUR LA FEUILLE ──────────────────────
     `--cmq-dy` et la classe `cmq-drag` vivaient sur `<html>`. Une propriété
     personnalisée est HÉRITÉE : la reposer sur la racine invalide le style de
     TOUS les nœuds du document à chaque `pointermove` - la grille, ses
     trente-six cartes, la barre de filtres - pour déplacer un seul élément.
     Elles vivent maintenant sur `.cmq`, le seul élément que le geste concerne,
     et le CSS les lit au même endroit (`.cmq.is-drag`). Le geste reste un
     `transform` et rien d'autre : aucune propriété de mise en page n'est
     touchée, et RIEN N'EST LU pendant le mouvement, donc aucune reprise de mise
     en page forcée. */
  function qdMove(dy) {
    var s = qvEl();
    if (s) s.style.setProperty('--cmq-dy', Math.round(dy) + 'px');
  }

  /* Le retour au repos, et il sert deux fois : à la fin d'un geste et à chaque
     fermeture (la feuille ne doit jamais garder un décalage de glissement pour
     sa réouverture). */
  function qdRest(s) {
    var el = s || qvEl();
    if (!el) return;
    el.classList.remove('is-drag');
    el.style.removeProperty('--cmq-dy');
  }

  function qdScroller() {
    var s = qvEl();
    return s ? s.querySelector('[data-cmq-scroll]') : null;
  }

  /* Le départ, écrit UNE fois pour les deux sources d'événements. Il rend `true`
     quand la feuille prend le geste en charge. */
  function qdBegin(target, x, y, t, id) {
    if (qd.on) return true;
    if (!qvIsOpen || !qvPhone()) return false;
    if (!target || !target.closest) return false;
    /* ── VAGUE 21 · LE VOILE EST UNE ZONE DE GESTE, PAS UN MUR ─────────────
       Au téléphone, la feuille occupe 92 % de la hauteur : au-dessus d'elle il
       reste une bande de 8 %, et c'est exactement là que tombe le pouce quand
       on tient le téléphone haut - au niveau de l'encoche. Un glissement qui
       partait de là ne trouvait NI poignée NI corps défilant : `qdBegin`
       rendait `false`, la feuille ne bougeait pas d'un pixel, et le geste
       repartait au navigateur.

       Le voile prend donc le geste comme la poignée le prend : armé tout de
       suite, sans seuil de tap à franchir, parce qu'il n'y a rien d'autre à
       faire sur un voile que de le pousser ou de le taper. LE TAP CONTINUE DE
       FERMER : un tap n'émet aucun `touchmove`, `qdFinish` le voit à 0 px et
       repose la feuille, puis le `click` arrive sur `[data-cmq-close]` comme
       avant. Les deux gestes coexistent sans se marcher dessus. */
    var onVeil = target.id === 'CmqVeil' || !!target.closest('.cmq-veil');
    var onGrab = onVeil || !!target.closest('[data-cmq-grab]');
    var scroller = qdScroller();
    var onBody = !onGrab && scroller && scroller.contains(target);
    if (!onGrab && !onBody) return false;
    if (onBody && scroller.scrollTop > 0) return false;
    qd.on = true;
    qd.armed = onGrab;
    qd.id = id;
    qd.x0 = x;
    qd.y0 = y;
    qd.t0 = t;
    qd.shots = !!target.closest('[data-cmq-shots]');
    qdTrail.length = 0;
    qdSample(y, t);
    if (onGrab) qvEl().classList.add('is-drag');
    return true;
  }

  /* La décision, elle aussi écrite une fois. Elle rend `true` quand le geste est
     le nôtre, c'est-à-dire quand il faut à la fois déplacer la feuille ET
     interdire à Safari de faire autre chose. */
  function qdConsider(x, y, t) {
    if (!qd.on) return false;
    if (qd.armed) return true;
    var dy = y - qd.y0;
    /* Depuis le corps, on n'accapare le geste qu'une fois le seuil de tap
       franchi ET seulement vers le bas : un défilement vers le haut reste un
       défilement. */
    if (dy <= QD_SLOP) {
      if (dy < -QD_SLOP) qd.on = false;
      return false;
    }
    /* Un balayage qui part de côté sur les photos appartient à la galerie. */
    if (qd.shots && Math.abs(x - qd.x0) > dy) {
      qd.on = false;
      return false;
    }
    var scroller = qdScroller();
    if (scroller && scroller.scrollTop > 0) {
      qd.on = false;
      return false;
    }
    qd.armed = true;
    qvEl().classList.add('is-drag');
    return true;
  }

  function qdStep(x, y, t) {
    if (!qdConsider(x, y, t)) return false;
    qdSample(y, t);
    qdMove(qdOffset(y - qd.y0));
    return true;
  }

  /* La fin. `close` est vrai pour un relâchement, faux pour une annulation :
     une annulation repose la feuille et ne décide rien. */
  function qdFinish(y, t, close) {
    if (!qd.on) return;
    var armed = qd.armed;
    qd.on = false;
    qd.armed = false;
    qd.id = null;
    /* Le retour élastique EST le retrait de `--cmq-dy` : la transition de la
       feuille reprend dans la même image et ramène le `transform` à zéro. Rien
       à animer à la main, rien à programmer. */
    qdRest(null);
    if (!armed || !close) { qdTrail.length = 0; return; }
    var dy = y - qd.y0;
    var v = qdSpeed(y, t);
    qdTrail.length = 0;
    if (dy > 0 && (dy >= QD_DISTANCE || v > QD_VELOCITY)) qvClose();
  }

  function qdAbort() {
    if (!qd.on) return;
    qd.on = false;
    qd.armed = false;
    qd.id = null;
    qdTrail.length = 0;
    qdRest(null);
  }

  /* ── LE CHEMIN DU DOIGT ────────────────────────────────────────────────
     Il est prioritaire sur celui du pointeur : c'est le seul qui peut dire non
     au défilement natif. `touchstart` en capture pour être servi avant tout
     écouteur de la page, `touchmove` NON PASSIF pour avoir le droit d'appeler
     `preventDefault()`.

     DEUX DOIGTS NE GLISSENT PAS UNE FEUILLE : c'est un pincement, et il
     appartient au navigateur (le zoom de page reste possible, c'est une règle
     d'accessibilité). Le geste en cours est abandonné, la feuille revient. */
  document.addEventListener(
    'touchstart',
    function (event) {
      if (event.touches.length > 1) { qdAbort(); return; }
      var t = event.touches[0];
      qdBegin(event.target, t.clientX, t.clientY, event.timeStamp, 'touch');
    },
    true
  );

  document.addEventListener(
    'touchmove',
    function (event) {
      if (!qd.on) return;
      if (event.touches.length > 1) { qdAbort(); return; }
      var t = event.touches[0];
      if (!qdStep(t.clientX, t.clientY, event.timeStamp)) return;
      /* C'est ICI, et nulle part ailleurs, que le défilement natif et le rebond
         élastique de WebKit sont arrêtés. */
      if (event.cancelable) event.preventDefault();
    },
    { passive: false, capture: true }
  );

  document.addEventListener(
    'touchend',
    function (event) {
      if (!qd.on) return;
      var t = event.changedTouches[0];
      qdFinish(t ? t.clientY : qd.y0, event.timeStamp, true);
    },
    true
  );

  document.addEventListener('touchcancel', qdAbort, true);

  /* ── LE CHEMIN DU POINTEUR ─────────────────────────────────────────────
     Il reste pour la souris et le stylet, et pour les navigateurs qui n'émettent
     pas d'événements tactiles. Quand le doigt a déjà pris le geste en charge,
     `qdBegin` rend `true` sans rien refaire et `qdStep` est idempotent : les
     deux chemins ne peuvent pas déplacer la feuille deux fois. */
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (event.pointerType === 'touch' && qd.on) return;
      qdBegin(event.target, event.clientX, event.clientY, event.timeStamp, event.pointerId);
    },
    true
  );

  document.addEventListener('pointermove', function (event) {
    if (!qd.on) return;
    if (qd.id !== 'touch' && event.pointerId !== qd.id) return;
    qdStep(event.clientX, event.clientY, event.timeStamp);
  });

  document.addEventListener('pointerup', function (event) {
    if (!qd.on) return;
    if (qd.id !== 'touch' && event.pointerId !== qd.id) return;
    qdFinish(event.clientY, event.timeStamp, true);
  });

  /* Une annulation ne ferme JAMAIS : voir le point 3 en tête de ce bloc. */
  document.addEventListener('pointercancel', qdAbort);

  /* ══════════════════════════════════════════════════════════════════════
     7. LE PONT VERS LE VISUALISEUR                      VAGUE 13, 28/08/2026
     ----------------------------------------------------------------------
     Le thème n'appelle QUE l'interface écrite dans `CONTRAT-VISUALISEUR.md` :
     `open`, `warm`, `hasRoom`, `clearRoom`, `roomThumb`, `captureRoom`, et les
     événements `room-set` / `room-cleared`. Aucune fonction interne du widget,
     aucun accès à son cache de rendus, et jamais le thème n'affiche lui-même
     un rendu : tout passe par `open()`.

     ── QUAND LE SCRIPT EST CHARGÉ, ET POURQUOI PAS APRÈS `load` ─────────────
     Le contrat demande « defer, après load ; il ne doit rien faire tant
     qu'aucune méthode n'est appelée (zéro requête au chargement) ». MESURE DU
     28/08 sur `visualizer.js` en production (version 2026-08-28, 80 Ko) : la
     condition N'EST PAS TENUE. Au chargement, le script

       · injecte 7 Ko de balisage de modale dans `body`
         (`document.body.insertAdjacentHTML`, ligne 228), qui contient
         une balise image pointant `…/casemira-logo.png` - c'est-à-dire UNE REQUÊTE vers
         Render, avant toute intention du visiteur ;
       · pose une classe sur `body` ;
       · arme un écouteur `pointermove` qui appelle `warm()` dès que le pointeur
         passe à moins de 160 px d'un de ses déclencheurs.

     Le brief prévoyait exactement ce cas : « si le script actuel injecte des
     choses au chargement, charge-le seulement au premier survol/tap d'un bouton
     Voir chez vous ou warm() ». C'est donc ce que fait ce fichier. Sur une
     collection, une page qui ne sera jamais suivie d'un clic sur « Voir chez
     vous » ne demande RIEN à Render.

     ── LE RÉVEIL DU SERVEUR N'EST PAS LE CHARGEMENT DE L'APP ────────────────
     Deux signaux, deux réponses, et c'est ce qui permet de tenir les deux
     demandes du brief à la fois :

       · APPROCHE (la carte « Voyez-le chez vous » entre dans l'écran) → un
         simple GET `/api/warm` (204 en 20 à 100 ms, aucun appel Gemini, avant
         les limiteurs). Le serveur se réveille, l'app ne se charge pas. Un
         visiteur qui fait défiler une collection ne paie pas 80 Ko pour une
         carte qu'il ne touchera peut-être jamais.
       · INTENTION (le pointeur entre sur un bouton, ou un doigt s'y pose) →
         le réveil ET le chargement de l'app, pour que le clic qui suit trouve
         `CasemiraVision.open` déjà là.

     ── LE REPLI EST CELUI DE LA VAGUE 12, ET IL EST INTACT ──────────────────
     Les boutons sont des `<a href="…?visualiser=1">`. Tant que rien n'est
     chargé, le lien fonctionne tout seul : la fiche s'ouvre et sa façade y
     ouvre le visualiseur. Le clic n'est intercepté que dans deux cas - l'app
     est prête (on appelle `open()` sur place), ou elle est en cours de
     chargement (on attend, et on suit le lien si le chargement échoue).
     ══════════════════════════════════════════════════════════════════════ */

  var VIZ_IDLE = 0;
  var VIZ_LOADING = 1;
  var VIZ_READY = 2;
  var vizState = VIZ_IDLE;
  var vizQueue = [];
  var vizPinged = false;

  function vizBase() {
    var s = qvEl();
    return s ? s.getAttribute('data-cm-viz-base') || '' : '';
  }

  function vision() {
    return window.CasemiraVision;
  }

  function vizHas(name) {
    var v = vision();
    return !!(v && typeof v[name] === 'function');
  }

  function vizFlush(ok) {
    var q = vizQueue;
    vizQueue = [];
    for (var i = 0; i < q.length; i++) {
      try {
        q[i](ok);
      } catch (e) {
        /* un rappel qui échoue ne doit pas empêcher les suivants */
      }
    }
  }

  function vizLoad(callback) {
    if (vizHas('open')) {
      vizState = VIZ_READY;
      if (callback) callback(true);
      return;
    }
    if (callback) vizQueue.push(callback);
    if (vizState === VIZ_LOADING) return;
    var base = vizBase();
    if (!base) {
      vizFlush(false);
      return;
    }
    vizState = VIZ_LOADING;

    var css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = base + '/visualizer.css';
    document.head.appendChild(css);

    /* Le nom de fichier est gardé dans le `src` : `visualizer.js` retrouve
       l'origine de ses propres ressources avec
       `document.querySelector('script[src*="visualizer.js"]')`. */
    var js = document.createElement('script');
    js.src = base + '/visualizer.js';
    js.async = true;
    js.onload = function () {
      vizState = VIZ_READY;
      syncRoom();
      vizFlush(true);
    };
    js.onerror = function () {
      /* Render indisponible : on redonne sa chance au geste suivant, et les
         rappels en attente repartent sur le lien `?visualiser=1`. */
      vizState = VIZ_IDLE;
      vizFlush(false);
    };
    document.head.appendChild(js);
  }

  /* Le réveil du serveur, SANS l'app. Une seule fois par page : le serveur
     reste chaud quinze minutes, et le widget a son propre dédoublonnage de
     quatre minutes par tapis quand c'est lui qui appelle. */
  function vizPing(rug) {
    if (vizHas('warm')) {
      try {
        vision().warm(rug ? { rugImageUrl: rug } : undefined);
      } catch (e) {
        /* une méthode absente ou en erreur ne doit rien casser ici */
      }
      return;
    }
    if (vizPinged) return;
    vizPinged = true;
    var base = vizBase();
    if (!base || !window.fetch) return;
    var url = base + '/api/warm' + (rug ? '?rug=' + encodeURIComponent(absUrl(rug)) : '');
    try {
      window
        .fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store', keepalive: true })
        .catch(function () {});
    } catch (e) {
      /* pas de fetch : l'app chauffera elle-même */
    }
  }

  function vizIntent(rug) {
    vizLoad();
    vizPing(rug);
  }

  /* La charge d'un bouton : celle de sa carte, celle du panneau ouvert, ou
     rien. Le panneau lit `qvData` et non la carte, parce que le visiteur peut
     avoir filtré la grille pendant que le panneau est ouvert. */
  function vizDataOf(el) {
    if (el.closest('.cmq')) return qvData;
    var card = el.closest('.cm-card');
    return card ? qvRead(card) : null;
  }

  /* Le contrat, mot pour mot. `rugInfo.nom` et `rugInfo.dimensions` sont
     RECONSTRUITS ici à partir de ce que la charge porte déjà : les écrire deux
     fois par carte coûterait 3 Ko de HTML par page pour zéro information. */
  function vizPayload(d, source) {
    var flat = d.v || (d.i && d.i[0]) || '';
    var g = d.g || {};
    var dims = (d.s || [])
      .slice(0, 6)
      .map(function (row) {
        return String(row[0]).replace(/\s/g, '');
      })
      .join(', ');
    var out = {
      rugImageUrl: absUrl(withWidth(flat, 1200)),
      rugName: d.n || '',
      rugType: d.t || '',
      productUrl: absUrl(bareUrl(d.u)),
      source: source,
      rugInfo: {
        nom: d.n || '',
        forme: g.f || '',
        composition: g.c || '',
        epaisseur: g.e || '',
        dimensions: dims,
        description: qvText(g.d),
      },
    };
    if (d.x && d.x.length) {
      out.rugImageUrls = d.x.map(function (u) {
        return absUrl(u);
      });
    }
    var cat = vizCatalog(d);
    if (cat.length) out.catalog = cat;
    return out;
  }

  /* ── 7 bis. LE CATALOGUE PASSÉ À `open()`         VAGUE 14, 28/08/2026 ────
     Le widget en production depuis le 28/08 à 02 h 23 lit `open({ …, catalog })`
     et s'en sert pour « Essayer un autre tapis de la collection » : sans lui,
     ce bouton n'a rien à proposer et le visiteur doit refermer la modale pour
     essayer le tapis d'à côté - c'est-à-dire perdre son rendu.

     LA SOURCE EST CE QUI EST RENDU, ET RIEN D'AUTRE. On lit les charges
     d'aperçu (`template[data-cm-qv]`) des cartes réellement présentes dans la
     grille : elles sont donc filtrées, triées et paginées comme la page l'est.
     Aucune requête, aucune donnée inventée.

     VINGT-QUATRE AU PLUS, ET C'EST LE PLAFOND DU WIDGET. Au-delà, il ignore la
     queue ; en dessous de douze, une grille filtrée serré n'a de toute façon
     pas plus à offrir. La carte OUVERTE est retirée de la liste : proposer
     d'essayer le tapis qu'on est en train de regarder n'est pas une proposition.

     LE CONTRAT EST TENU ENTRÉE PAR ENTRÉE : `rugImageUrl` est TOUJOURS présent
     (les charges qui n'ont pas d'image sont écartées ici, elles seraient
     ignorées là-bas), et `productUrl` est absolu. `thumb` et `rugType` sont
     facultatifs, on les donne quand on les a. */
  var CATALOG_MAX = 24;

  function vizCatalogEntry(d) {
    var flat = d.v || (d.i && d.i[0]) || '';
    if (!flat) return null;
    var g = d.g || {};
    var dims = (d.s || [])
      .slice(0, 6)
      .map(function (row) {
        return String(row[0]).replace(/\s/g, '');
      })
      .join(', ');
    var out = {
      rugImageUrl: absUrl(withWidth(flat, 1200)),
      rugName: d.n || '',
      productUrl: absUrl(bareUrl(d.u)),
      rugInfo: {
        nom: d.n || '',
        forme: g.f || '',
        composition: g.c || '',
        epaisseur: g.e || '',
        dimensions: dims,
        description: qvText(g.d),
      },
    };
    if (d.t) out.rugType = d.t;
    if (d.i && d.i[0]) out.thumb = absUrl(withWidth(d.i[0], 400));
    if (d.x && d.x.length) {
      out.rugImageUrls = d.x.map(function (u) {
        return absUrl(u);
      });
    }
    return out;
  }

  function vizCatalog(current) {
    var g = grid();
    if (!g) return [];
    var cards = g.querySelectorAll('.cm-card');
    var out = [];
    var here = current ? bareUrl(current.u) : '';
    for (var i = 0; i < cards.length && out.length < CATALOG_MAX; i++) {
      var d = qvRead(cards[i]);
      if (!d) continue;
      if (here && bareUrl(d.u) === here) continue;
      var entry = vizCatalogEntry(d);
      if (entry) out.push(entry);
    }
    return out;
  }

  /* ★ VAGUE 16 · LE LANCEMENT DU RENDU EST SORTI DE SON ÉCOUTEUR.
     Le mode essayage (§ 9 bis) ouvre le visualiseur depuis un clic sur la PHOTO
     et non sur un bouton : il lui faut le même chemin - widget prêt, widget en
     cours de chargement, widget absent - et le dupliquer, ce serait signer pour
     tenir deux chemins d'accord. Rien n'a changé dans ce qu'il fait ; il a
     seulement un nom et deux appelants.

     `fallback` est l'adresse où aller si Render ne répond pas. Elle vaut le
     `href` du bouton pour un bouton, la fiche avec `?visualiser=1` pour une
     photo. Elle peut être vide : on ne va alors nulle part, ce qui est mieux
     que d'aller sur `#`. */
  function vizGo(d, source, fallback) {
    if (!d) return false;

    if (vizHas('open')) {
      /* Ouvrir le visualiseur depuis l'aperçu, c'est passer d'une surcouche à
         une autre : on referme la nôtre, sinon deux verrous de défilement se
         superposeraient et le retour du visualiseur laisserait la page bloquée. */
      if (qvIsOpen) qvClose();
      vision().open(vizPayload(d, source));
      return true;
    }

    vizLoad(function (ok) {
      if (ok && vizHas('open')) {
        if (qvIsOpen) qvClose();
        vision().open(vizPayload(d, source));
      } else if (fallback && fallback !== '#') {
        window.location.href = fallback;
      }
    });
    return true;
  }

  /* ★ VAGUE 17 · CABINE ÉTEINTE, LE PONT N'EST PAS ARMÉ. Les boutons
     `[data-cm-viz]` ne sont plus rendus, mais un écouteur de document survit à
     ce que le Liquid ne rend pas : on ne l'installe donc pas du tout. */
  if (CABINE) document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-cm-viz]');
    if (!btn) return;
    var d = vizDataOf(btn);
    if (!d) return; /* pas de charge : le lien `?visualiser=1` fait son travail */
    var source = btn.getAttribute('data-cm-viz') || 'collection-card';

    if (vizHas('open') || vizState === VIZ_LOADING) {
      event.preventDefault();
      vizGo(d, source, btn.getAttribute('href'));
      return;
    }
    /* Ni chargé ni en cours : on laisse le lien partir vers la fiche avec
       `?visualiser=1`. C'est le repli de la vague 12, intact. */
  });

  function onVizHint(event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var el = t.closest('[data-cm-viz], [data-cm-viz-warm]');
    if (!el) return;
    var d = vizDataOf(el);
    vizIntent(d ? withWidth(d.v || '', 1200) : '');
  }

  /* Le survol qui réveille Render. Cabine éteinte, il n'y a rien à réveiller :
     c'est LA ligne qui décide qu'une page de collection ne parle jamais à
     l'hôte du visualiseur. */
  if (CABINE) {
    ['pointerover', 'touchstart', 'focusin'].forEach(function (ev) {
      document.addEventListener(ev, onVizHint, { capture: true, passive: true });
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
     8. « MA PIÈCE »                                     VAGUE 13, 28/08/2026
     ----------------------------------------------------------------------
     Le thème LIT l'état de la mémoire de pièce, il ne l'écrit jamais. Deux
     sources, dans cet ordre :

       1. `CasemiraVision.hasRoom()` quand le widget est chargé - la seule
          source qui fasse autorité ;
       2. sinon la clé `cm-vision-room` du stockage local, LUE et jamais
          modifiée. C'est le repli prévu par le brief, et il est nécessaire :
          le widget n'est pas chargé au repos (§ 7), et la carte doit pourtant
          dire la vérité dès le premier octet.

     LE FORMAT EST CELUI DU WIDGET, relevé le 28/08 dans `visualizer.js`
     (`loadRoom`) : un objet `{ v, t, source, aspect, dataUrl, thumb }`, valide
     si `dataUrl` et `t` sont là et si `t` a moins de sept jours. On applique la
     MÊME règle - un enregistrement périmé ne compte pas - mais on ne le
     SUPPRIME pas : effacer la mémoire du widget serait décider à sa place.

     LE STOCKAGE PEUT ÊTRE REFUSÉ (navigation privée iOS, réglages de
     confidentialité) : `localStorage` lève alors une exception à la simple
     lecture. Tout est donc sous `try`, et la réponse par défaut est « pas de
     pièce » - c'est-à-dire l'état qui fonctionne sans rien.
     ══════════════════════════════════════════════════════════════════════ */

  var ROOM_KEY = 'cm-vision-room';
  var ROOM_TTL_MS = 7 * 24 * 3600 * 1000;

  function hasRoom() {
    if (vizHas('hasRoom')) {
      try {
        return !!vision().hasRoom();
      } catch (e) {
        /* on retombe sur la lecture directe */
      }
    }
    try {
      var raw = window.localStorage.getItem(ROOM_KEY);
      if (!raw) return false;
      var rec = JSON.parse(raw);
      if (!rec || !rec.dataUrl || !rec.t) return false;
      return Date.now() - rec.t <= ROOM_TTL_MS;
    } catch (e) {
      return false;
    }
  }

  function syncRoom() {
    var on = hasRoom();
    document.documentElement.classList.toggle('cm-has-room', on);
    /* VAGUE 14 · le bandeau guidé suit le même état, et il est construit ICI
       plutôt qu'en Liquid : il n'existe que pour un visiteur qui a une pièce en
       mémoire, c'est-à-dire jamais dans le HTML servi par le cache. */
    syncRoomBar(on);
    var url = '';
    if (on && vizHas('roomThumb')) {
      try {
        url = vision().roomThumb() || '';
      } catch (e) {
        url = '';
      }
    }
    /* Deux vignettes, un seul appel : celle de la carte et celle du bandeau.
       Une seule lecture de `roomThumb()`, deux poses. */
    document.querySelectorAll('[data-cm-room-thumb-slot]').forEach(function (slot) {
      paintThumb(slot, url);
    });
  }

  /* L'IMAGE N'EXISTE QUE QUAND ELLE A UNE PHOTO, et `hidden` ne suffisait
     pas. Mesuré sur le preview après le push de la vague 14 : la carte portait
     une balise image sans adresse (`currentSrc` vide, `naturalWidth` 0), c'est-à-dire
     une image cassée que seul `hidden` empêchait de se voir - un lecteur
     d'écran, un vérificateur de page ou un `document.images` la trouvaient
     quand même.

     Le Liquid ne rend donc plus qu'un EMPLACEMENT vide, et c'est ici qu'une
     image y entre ou en sort. Une image sans source n'existe à aucun moment, et
     c'est la seule façon de le garantir : un attribut qu'on n'écrit pas ne peut
     pas être écrit de travers.

     L'emplacement porte la CLASSE que l'image doit prendre (`cmv__thumb` dans la
     carte, `cmg__thumb` dans le bandeau) : les deux tailles restent décidées
     par la feuille de style, et ce fichier ne connaît aucune dimension. */
  function paintThumb(slot, url) {
    var img = slot.firstElementChild;
    if (!url) {
      if (img) slot.removeChild(img);
      return;
    }
    if (!img) {
      img = document.createElement('img');
      img.className = slot.getAttribute('data-cm-room-thumb-slot') || '';
      img.alt = '';
      slot.appendChild(img);
    }
    if (img.getAttribute('src') !== url) img.setAttribute('src', url);
  }

  /* ★ VAGUE 17 · aucune écoute de `casemira-vision:*` cabine éteinte : le
     bandeau, la vignette de pièce et les libellés qui basculent n'existent que
     pour elle. */
  if (CABINE) {
    document.addEventListener('casemira-vision:room-set', syncRoom);
    document.addEventListener('casemira-vision:room-cleared', syncRoom);
  }

  /* « Photographier ma pièce ». Quand le widget expose `captureRoom`, on
     l'appelle. Quand il ne l'expose pas encore, on ouvre la feuille « Comment
     ça marche » TOUT DE SUITE - et non après avoir attendu 80 Ko et un
     démarrage à froid pour découvrir que la méthode manque. Le chargement part
     quand même en arrière-plan : le geste suivant trouvera la vraie porte. */
  function captureRoom(source) {
    if (vizHas('captureRoom')) {
      try {
        vision().captureRoom({ source: source });
        return;
      } catch (e) {
        /* on retombe sur l'explication */
      }
    }
    vizLoad();
    var opener = document.querySelector('[data-cmx-open]');
    if (opener) openSheet(opener);
  }

  if (CABINE) document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;

    if (t.closest('[data-cm-room-capture]')) {
      event.preventDefault();
      captureRoom('collection-hero');
      return;
    }
    if (t.closest('[data-cm-room-change]')) {
      event.preventDefault();
      if (vizHas('clearRoom')) {
        try {
          vision().clearRoom();
        } catch (e) {
          /* l'événement `room-cleared` ne viendra pas : on resynchronise */
        }
      }
      syncRoom();
      captureRoom('collection-hero');
    }
  });

  /* L'APPROCHE DE LA CARTE RÉVEILLE LE SERVEUR, ET RIEN D'AUTRE. 200 px de
     marge : le temps que la carte arrive sous les yeux, le service est chaud.
     `unobserve` tout de suite - une seule fois, littéralement. */
  var roomIo = null;

  function observeRoomWarm() {
    if (!window.IntersectionObserver) return;
    if (!roomIo) {
      roomIo = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            roomIo.unobserve(e.target);
            vizPing('');
          });
        },
        { rootMargin: '200px 0px' }
      );
    }
    document.querySelectorAll('.cmv').forEach(function (n) {
      roomIo.observe(n);
    });
  }

  /* ══════════════════════════════════════════════════════════════════════
     9. LE MODE « MA PIÈCE » GUIDÉ                       VAGUE 14, 28/08/2026
     ----------------------------------------------------------------------
     Retour de Raph le 28/08 : « une fois la pièce photographiée, guider vers
     Dans ma pièce ». Le thème savait déjà qu'une pièce existait - la vague 13
     basculait trente-sept libellés d'une classe posée sur la racine - mais il
     ne le DISAIT nulle part, et rien n'indiquait quoi en faire.

     TROIS OBJETS, ET AUCUN N'EST UN ÉTAT DU THÈME :
       · un bandeau fin sous la barre de filtres, qui dit l'état et donne la
         sortie (« Changer de photo ») ;
       · une bulle-guide, posée sur le bouton de la première carte visible,
         quand le WIDGET dit que c'est le moment ;
       · une pulsation unique de ce bouton.

     LE MOMENT DU GUIDAGE APPARTIENT AU WIDGET, et c'est le complément du 28/08
     à 02 h 23 : il émet `room-set` avec `{ guide: true }` UNE FOIS PAR SESSION,
     au clic sur « Choisir un tapis ». Le premier `room-set` - celui de la
     mémorisation de la photo - ne porte pas `guide`. On teste donc
     `detail.guide === true` et rien d'autre : ni un compteur à nous, ni une
     heuristique. Deux mémoires du même événement finissent toujours par se
     contredire.

     LE THÈME NE MÉMORISE QU'UNE CHOSE : LE REFUS. Le bandeau fermé reste fermé
     pour la session (`sessionStorage`), parce que refermer deux fois la même
     bande est la définition d'une nuisance. Et `sessionStorage` peut lever à la
     simple lecture (navigation privée iOS) : tout est sous `try`, et la réponse
     par défaut est « pas fermé », c'est-à-dire l'état qui fonctionne sans rien.
     ══════════════════════════════════════════════════════════════════════ */

  var BAR_OFF_KEY = 'cm-room-bar-off';

  function barDismissed() {
    try {
      return window.sessionStorage.getItem(BAR_OFF_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function dismissBar() {
    try {
      window.sessionStorage.setItem(BAR_OFF_KEY, '1');
    } catch (e) {
      /* stockage refusé : le bandeau reviendra au prochain rendu, tant pis */
    }
  }

  /* LA BARRE DE FILTRES A DEUX INCARNATIONS et une seule est rendue à la fois :
     `.cmm-bar` au téléphone, `.cmc__sticky` sur ordinateur. On ne devine pas
     laquelle d'après la largeur - c'est le CSS qui décide, et il peut changer
     d'avis. On prend celle qui a une hauteur et qui colle vraiment. */
  function stickyBar() {
    var cands = [document.querySelector('.cmm-bar'), document.querySelector('.cmc__sticky')];
    for (var i = 0; i < cands.length; i++) {
      var b = cands[i];
      if (!b) continue;
      if (b.getBoundingClientRect().height <= 0) continue;
      if (window.getComputedStyle(b).position !== 'sticky') continue;
      return b;
    }
    return null;
  }

  var CLOSE_SVG =
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  /* ★ VAGUE 16, point 2 · LE BANDEAU EST UN ENFANT DE LA BARRE.
     La vague 14 en faisait un frère collant, avec un `top` LU UNE FOIS au repos.
     La barre, elle, ne tient pas son `top` : sur ordinateur elle se retire en
     `transform` avec l'en-tête (`html.cmc-collee`, vague 11), au téléphone son
     `top` change de valeur selon trois classes de `body`. Une position lue une
     fois ne peut pas suivre quatre états et une transition de 500 ms : le
     bandeau se détachait et flottait au milieu de l'écran (capture de Raph du
     29/08 22h04).

     Il n'a donc plus de position du tout. Placé DANS la barre, en dernier, il
     hérite de sa position collante, de son `top`, de son `transform` et de sa
     profondeur. Il ne peut plus dériver : il n'y a plus deux positions à tenir
     d'accord, il n'y en a qu'une. La feuille de style fait le reste (§ 8.1 de
     `casemira-collection-plus.css`). */
  function buildRoomBar() {
    var el = document.querySelector('.cmg');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'cmg';
    el.setAttribute('data-cm-room-bar', '');
    el.hidden = true;
    el.innerHTML =
      '<span class="cmg__thumb-slot" data-cm-room-thumb-slot="cmg__thumb"></span>' +
      '<button type="button" class="cmg__try" role="switch" aria-checked="true" data-cm-try-toggle>' +
      '<span class="cmg__sw" aria-hidden="true"></span>' +
      '<span class="cmg__try-t">' + esc(S.modeEssayage || 'Mode essayage') + '</span>' +
      '</button>' +
      '<span class="cmg__txt"><b>' + esc(S.piecePrete || 'Votre pièce est prête') + '</b>' +
      '<em class="cmg__on">' +
      esc(S.barreEssayageOn || " : touchez un tapis pour l'essayer. Les fiches restent accessibles par « Aperçu ».") +
      '</em>' +
      '<em class="cmg__off">' +
      esc(S.barreEssayageOff || ' : le mode essayage est éteint, un tapis touché ouvre sa fiche.') +
      '</em>' +
      '</span>' +
      '<button type="button" class="cmg__act" data-cm-room-change>' +
      esc(S.changerDePhoto || 'Changer de photo') +
      '</button>' +
      '<button type="button" class="cmg__close" data-cm-room-bar-close aria-label="' +
      esc(S.fermer || 'Fermer') +
      '">' +
      CLOSE_SVG +
      '</button>';
    return el;
  }

  /* LE BANDEAU CHANGE D'HÔTE QUAND L'ÉCRAN CHANGE DE FORME. Les deux barres
     existent dans le document en permanence ; c'est le CSS qui décide laquelle
     colle (`stickyBar()` le mesure au lieu de le deviner). À la rotation d'une
     tablette, l'hôte n'est plus le même : on redéplace, et comme c'est le MÊME
     nœud qui se déplace, l'état de la bascule et la vignette suivent. */
  function placeRoomBar(el) {
    var bar = stickyBar();
    if (!bar) return;
    /* Toujours en DERNIER dans la barre : sous la rangée de commandes, jamais
       au-dessus d'elle. */
    if (el.parentNode !== bar || bar.lastElementChild !== el) bar.appendChild(el);
  }

  function syncRoomBar(on) {
    if (!on || barDismissed()) {
      var gone = document.querySelector('.cmg');
      if (gone) gone.hidden = true;
      return;
    }
    var el = buildRoomBar();
    placeRoomBar(el);
    if (!el.parentNode) return;
    el.hidden = false;
    syncTryUi();
  }

  if (CABINE) document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    if (!t.closest('[data-cm-room-bar-close]')) return;
    event.preventDefault();
    dismissBar();
    /* FERMER LE BANDEAU ÉTEINT LE MODE, et c'est la sortie évidente que le
       retour demande : la seule commande visible du mode part avec lui, il ne
       peut donc pas rester allumé sans rien pour l'éteindre. */
    setTryOn(false);
    var el = document.querySelector('.cmg');
    if (el) el.hidden = true;
  });

  /* ══════════════════════════════════════════════════════════════════════
     9 bis. LE MODE ESSAYAGE                              VAGUE 16, 29/08/2026
     ----------------------------------------------------------------------
     « Taper n'importe où sur une carte doit essayer le tapis. » Le mode est un
     OPT-IN : il n'existe qu'après une capture de pièce, il s'allume tout seul à
     ce moment-là (c'est ce que le visiteur vient de demander), et il s'éteint
     d'un tap sur la bascule du bandeau ou en fermant le bandeau.

     L'ÉTAT EST UNE CLASSE SUR `<html>` ET UNE CLÉ DE SESSION. La classe fait
     basculer les trente-six cartes d'un coup, y compris celles qu'un rendu AJAX
     de filtre vient de reconstruire - c'est la mécanique du § 2 ter, et elle
     vaut ici pour la même raison. La clé de session mémorise le REFUS et le
     consentement pour la durée de la visite, jamais au-delà : un mode qui
     survit à la fermeture de l'onglet est un mode qu'on ne comprend plus en
     revenant.

     CE QUI N'EST JAMAIS INTERCEPTÉ :
       · la touche Entrée sur un lien de carte. Un clic clavier arrive avec
         `event.detail === 0` ; le clic de pointeur, lui, vaut 1 ou plus. C'est
         la seule distinction fiable, et elle est dans la norme UI Events. Le
         clavier mène donc TOUJOURS à la fiche, et personne n'est piégé ;
       · les commandes de la carte : le « + » de la mini-fiche, « Aperçu »,
         « Dans ma pièce », les lignes de format du bandeau de survol, le badge.
         Elles font déjà chacune une chose précise, et le mode ne la leur prend
         pas.
     ══════════════════════════════════════════════════════════════════════ */

  var TRY_KEY = 'cm-try-on';
  var TRY_CLASS = 'cm-try-on';

  /* Les commandes de la carte, celles qui gardent leur geste. Écrit une fois. */
  var TRY_SKIP =
    '.cm-qv-dot, .cm-qv-lnk, .cm-viz-fmt, .cm-fmt, .cm-formats__more, ' +
    '.casemira-badge, [data-cm-qv-open], [data-cm-viz], button, input, select, label';

  function tryOn() {
    return document.documentElement.classList.contains(TRY_CLASS);
  }

  function tryRemembered() {
    try {
      return window.sessionStorage.getItem(TRY_KEY);
    } catch (e) {
      return null;
    }
  }

  function rememberTry(on) {
    try {
      window.sessionStorage.setItem(TRY_KEY, on ? '1' : '0');
    } catch (e) {
      /* stockage refusé : le mode vaut pour la page, et c'est déjà l'essentiel */
    }
  }

  /* LE VOILE DES PHOTOS EST FABRIQUÉ À L'ALLUMAGE, PAS AU RENDU. Trente-six
     voiles rendus par le Liquid sur une page que la plupart des visiteurs voient
     sans jamais photographier leur pièce, ce sont trente-six nœuds payés pour
     rien. Ici, un seul balisage écrit dans ce fichier, cloné par carte. */
  var TRY_HOME =
    '<svg class="cm-try__i" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M3.5 10.5 12 4l8.5 6.5V19a1.5 1.5 0 0 1-1.5 1.5h-3.5v-6h-7v6H5A1.5 1.5 0 0 1 3.5 19z"' +
    ' stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function paintTry() {
    var g = grid();
    if (!g || !tryOn()) return;
    var cards = g.querySelectorAll('.cm-card');
    for (var i = 0; i < cards.length; i++) {
      var wrap = cards[i].querySelector('.product-card__image-wrapper');
      if (!wrap || wrap.querySelector('.cm-try')) continue;
      var veil = document.createElement('span');
      veil.className = 'cm-try';
      veil.setAttribute('aria-hidden', 'true');
      /* DEUX LIBELLÉS, ET LE CSS EN CHOISIT UN. Mesuré le 29/08 sur une carte de
         téléphone : la colonne fait 179 px, la pastille 147, et « Essayer dans
         ma pièce » y est coupé au milieu d'un mot. La phrase longue reste sur
         ordinateur, où elle tient ; au téléphone c'est « Dans ma pièce », le mot
         que le site emploie DÉJÀ partout ailleurs quand une pièce est mémorisée
         (§ 2 ter). Aucun troisième vocabulaire n'est inventé. */
      veil.innerHTML =
        '<span class="cm-try__p">' +
        TRY_HOME +
        '<span class="cm-try__t cm-try__t--long">' + esc(S.essayerLong || 'Essayer dans ma pièce') + '</span>' +
        '<span class="cm-try__t cm-try__t--short">' + esc(S.essayerCourt || 'Dans ma pièce') + '</span>' +
        '</span>';
      wrap.appendChild(veil);
    }
  }

  function syncTryUi() {
    var on = tryOn();
    var sw = document.querySelector('[data-cm-try-toggle]');
    if (sw) sw.setAttribute('aria-checked', on ? 'true' : 'false');
    if (on) paintTry();
  }

  function setTryOn(on) {
    document.documentElement.classList.toggle(TRY_CLASS, !!on);
    rememberTry(!!on);
    syncTryUi();
  }

  if (CABINE) document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var sw = t.closest('[data-cm-try-toggle]');
    if (!sw) return;
    event.preventDefault();
    setTryOn(!tryOn());
  });

  /* ── L'INTERCEPTION ────────────────────────────────────────────────────
     En phase de CAPTURE et sur la grille : elle passe donc avant le lien de la
     carte sans avoir à connaître ses écouteurs. `preventDefault` suffit à
     empêcher la navigation ; `stopPropagation` empêche en plus la mini-fiche de
     s'ouvrir par-dessus. */
  function tryClick(event) {
    if (!tryOn()) return;
    /* Clic clavier (Entrée, Espace) : `detail` vaut 0. On ne l'intercepte
       JAMAIS - la fiche doit rester atteignable au clavier. */
    if (!event.detail) return;
    var t = event.target;
    if (!t || !t.closest) return;
    var card = t.closest('.cm-card');
    if (!card) return;
    if (t.closest(TRY_SKIP)) return;
    var d = qvRead(card);
    if (!d) return;
    event.preventDefault();
    event.stopPropagation();
    closeGuide();
    tryOpen(card, d);
  }

  /* Le rendu, avec le catalogue : c'est le contrat du visualiseur, et c'est le
     MÊME chemin que le bouton « Dans ma pièce » de la carte (§ 7). On ne
     fabrique pas une seconde charge utile ici, ni un second chemin de
     chargement : `vizGo` est celui du § 7, sorti de son écouteur pour être
     appelé des deux endroits.

     LE REPLI EST LA FICHE. Si le widget ne répond pas, on part sur la fiche
     avec `?visualiser=1` : le geste du visiteur aboutit toujours, même quand
     Render dort. C'est exactement ce que fait le bouton. */
  function tryOpen(card, d) {
    vizGo(d, 'collection-try-on', bareUrl(d.u) + '?visualiser=1');
  }

  /* ── 9.1 La bulle-guide ────────────────────────────────────────────────
     Elle est en `position: fixed` et placée d'après la géométrie du bouton :
     ancrée dans le flux, elle aurait à traverser l'`overflow: hidden` de la
     photo de carte et y serait coupée.

     SUR ORDINATEUR, LE BOUTON QU'ELLE DÉSIGNE NE SE VOIT QU'AU SURVOL. Une
     bulle qui pointe un bouton invisible ne guide personne : on ouvre donc le
     bandeau de la première carte le temps du guidage (`cm-guide-open`), et on
     le referme avec la bulle.

     AU TÉLÉPHONE, le bouton est le « + » de la carte, et la phrase change :
     l'action n'est pas « toucher ce bouton pour voir le tapis » mais « ouvrir
     ce tapis pour l'essayer », parce que c'est l'aperçu qui porte « Dans ma
     pièce ». On dit ce qui va se passer, pas ce qu'on aimerait qu'il se passe.

     ELLE SE FERME AU PREMIER CLIC N'IMPORTE OÙ, y compris celui qui touche le
     bouton désigné : c'est le bon geste, il ne doit pas être avalé. La bulle est
     donc en `pointer-events: none` et l'écouteur est en phase de CAPTURE, ce qui
     le fait passer avant l'ouverture de l'aperçu sans rien lui prendre. */

  /* « La première carte visible », et la définition compte : celle qui a une
     hauteur ET qui intersecte l'écran. Un produit à taille unique n'a pas de
     bandeau de formats, donc pas de bouton à désigner sur ordinateur : on passe
     à la suivante plutôt que de renoncer au guidage. */
  function guideTarget(selector) {
    var g = grid();
    if (!g) return null;
    var cards = g.querySelectorAll('.cm-card');
    var h = window.innerHeight || 0;
    var fallback = null;
    for (var i = 0; i < cards.length; i++) {
      var r = cards[i].getBoundingClientRect();
      var btn = cards[i].querySelector(selector);
      if (!btn) continue;
      if (!fallback) fallback = { card: cards[i], btn: btn };
      if (r.height > 0 && r.bottom > 0 && r.top < h) return { card: cards[i], btn: btn };
    }
    return fallback;
  }

  var guideEl = null;
  var guideCard = null;
  var guideBtn = null;

  function closeGuide() {
    if (guideBtn) guideBtn.classList.remove('cm-guide-pulse');
    if (guideCard) guideCard.classList.remove('cm-guide-open');
    if (guideEl && guideEl.parentNode) guideEl.parentNode.removeChild(guideEl);
    guideEl = null;
    guideCard = null;
    guideBtn = null;
  }

  /* ★ VAGUE 16, point 3 · LA BULLE DÉSIGNE LE TAPIS, PLUS UN BOUTON.
     En vague 14 elle pointait le petit bouton « Dans ma pièce » du bandeau de
     survol (ordinateur) ou le « + » de la carte (téléphone), et sa phrase
     disait « touchez ICI ». Le mode essayage a changé la réponse à la question
     qu'elle pose : ce qu'il faut toucher, ce n'est plus un bouton, c'est LE
     TAPIS. Elle se pose donc sur la photo de la première carte visible, et elle
     dit la même chose des deux côtés de la charnière.

     Quand le mode est ÉTEINT (visiteur qui l'a refusé, puis nouvelle photo),
     elle reprend exactement le guidage de la vague 14 : le bouton, et la phrase
     du bouton. Deux modes, deux vérités, aucune des deux inventée. */
  function showGuide() {
    closeGuide();
    var phone = qvPhone();
    var mode = tryOn();
    var hit = guideTarget(
      mode ? '.product-card__image-wrapper' : phone ? '.cm-qv-dot' : '.cm-viz-fmt'
    );
    if (!hit) return false;
    var card = hit.card;
    var btn = hit.btn;

    guideCard = card;
    guideBtn = btn;
    if (!mode && !phone) card.classList.add('cm-guide-open');

    guideEl = document.createElement('div');
    guideEl.className = 'cmgb';
    guideEl.setAttribute('data-cm-guide', '');
    guideEl.setAttribute('role', 'status');
    guideEl.textContent = mode
      ? S.guideMode || "Touchez un tapis pour l'essayer dans votre pièce"
      : phone
        ? S.guideTelephone || "Ouvrez un tapis pour l'essayer dans votre pièce"
        : S.guideOrdinateur || 'Touchez ici pour voir ce tapis dans votre pièce';
    document.body.appendChild(guideEl);

    /* Une géométrie est lue APRÈS l'insertion et après l'ouverture du bandeau :
       un bouton replié n'a pas la position qu'il aura une trame plus tard. */
    var r = btn.getBoundingClientRect();
    var w = guideEl.getBoundingClientRect().width;
    var vw = document.documentElement.clientWidth;
    var left = Math.round(r.left + r.width / 2 - w / 2);
    left = Math.max(8, Math.min(left, vw - w - 8));
    var top = Math.round(r.top - guideEl.getBoundingClientRect().height - 10);
    var below = top < 8;
    if (below) top = Math.round(r.bottom + 10);
    guideEl.classList.toggle('is-below', below);
    guideEl.style.left = left + 'px';
    guideEl.style.top = top + 'px';
    guideEl.style.setProperty(
      '--cmgb-arrow',
      Math.round(Math.max(12, Math.min(r.left + r.width / 2 - left, w - 20))) + 'px'
    );

    /* La pulsation est faite pour un BOUTON de 24 px. Une photo de 424 px qui
       respire deux fois, c'est la grille entière qui bouge : en mode essayage,
       c'est le voile « Essayer dans ma pièce » qui porte déjà le repère, et la
       bulle qui porte la phrase. */
    if (!mode) btn.classList.add('cm-guide-pulse');
    window.requestAnimationFrame(function () {
      if (guideEl) guideEl.classList.add('is-in');
    });
    return true;
  }

  if (CABINE) {
    document.addEventListener(
      'click',
      function () {
        if (guideEl) closeGuide();
      },
      true
    );
  }

  /* `detail.guide === true` ET RIEN D'AUTRE. Le premier `room-set` - celui de la
     mémorisation de la photo - ne porte pas `guide` : la bulle n'apparaît donc
     pas au moment où le visiteur vient de photographier sa pièce, qui est
     précisément le moment où il regarde ailleurs. */
  if (CABINE) document.addEventListener('casemira-vision:room-set', function (event) {
    var d = event && event.detail;
    if (!d || d.guide !== true) return;
    /* La grille peut être en cours de re-rendu : on laisse passer une trame. */
    window.requestAnimationFrame(showGuide);
  });

  if (CABINE) document.addEventListener('casemira-vision:room-cleared', function () {
    closeGuide();
    /* Plus de pièce, plus de mode : essayer un tapis dans une pièce qui
       n'existe plus n'a pas de sens, et la bascule n'aurait plus de bandeau où
       vivre. */
    setTryOn(false);
  });

  /* ── L'ALLUMAGE DU MODE ────────────────────────────────────────────────
     ★ VAGUE 16, point 3 : ON par défaut JUSTE APRÈS LA CAPTURE, et à ce
     moment-là seulement. `detail.guide === true` est le signal que le widget
     envoie quand il vient de mémoriser une pièce et qu'il attend qu'on montre
     quoi en faire : c'est exactement l'instant où le mode a un sens.

     LE REFUS EST MÉMORISÉ, ET IL GAGNE. Un visiteur qui a éteint le mode ne le
     voit pas se rallumer à la photo suivante ; on ne rallume que ce qui n'a
     jamais été éteint. */
  if (CABINE) document.addEventListener('casemira-vision:room-set', function (event) {
    var d = event && event.detail;
    if (!d || d.guide !== true) return;
    if (tryRemembered() === '0') {
      syncTryUi();
      return;
    }
    setTryOn(true);
  });

  /* L'interception vit sur le DOCUMENT et en CAPTURE : la grille est détruite
     et reconstruite à chaque filtre, un écouteur posé dessus partirait avec
     elle. */
  if (CABINE) document.addEventListener('click', tryClick, true);

  /* « Voir 36 tapis de plus » ajoute des cartes SANS re-rendre la grille : il
     n'emet pas `collection:rerendered` mais `collection:loadedmore`
     (`casemira-collection-more.js`). Le COMPORTEMENT du mode les couvre deja -
     l'interception vit sur le document - mais leur VOILE, lui, se pose carte par
     carte. Sans cette ligne, les trente-six tapis suivants seraient essayables
     sans que rien ne le dise. */
  if (CABINE) document.addEventListener('collection:loadedmore', function () {
    syncTryUi();
  });

  /* ── 5. Armement ─────────────────────────────────────────────────────────
     `place()` tourne tôt (la rangée est une position, pas une animation, et
     une rangée fausse se verrait). Les observateurs, eux, attendent `load`. */

  /* Un rendu AJAX rapporte une feuille et un voile NEUFS dans la grille, avec
     les mêmes identifiants que ceux déjà sortis sur `body` : deux `#CmxSheet`
     dans le document, et `getElementById` qui répond celui de la grille. On
     garde le premier venu, on le sort sur `body`, et on retire les autres. */
  function dedupe(selector) {
    var all = document.querySelectorAll(selector);
    if (!all.length) return;
    for (var i = 1; i < all.length; i++) all[i].parentNode.removeChild(all[i]);
    detach(all[0]);
  }

  function refreshPlacement() {
    dedupe('.cmx-veil');
    dedupe('.cmx-sheet');
    place();
    if (!CABINE) return;
    var cmg = document.querySelector('.cmg');
    if (cmg && !cmg.hidden) placeRoomBar(cmg);
    /* ★ VAGUE 16 : la grille peut être neuve (rendu AJAX d'un filtre), le mode
       lui, ne l'est pas. Les voiles se reposent sur les cartes qui arrivent. */
    syncTryUi();
  }

  function arm() {
    refreshPlacement();
    observeRails();
    observeViz();
    /* ★ VAGUE 17 · cabine éteinte, ni réveil à l'approche ni chargement
       anticipé : c'est ici que se joue le « zéro requête vers l'hôte du
       visualiseur depuis une page collection ». */
    if (!CABINE) return;
    observeRoomWarm();
    /* UNE SEULE EXCEPTION AU CHARGEMENT PARESSEUX, ET ELLE EST CIBLÉE. Un
       visiteur qui a DÉJÀ une photo de pièce mémorisée a utilisé le
       visualiseur, et le contrat dit qu'avec une pièce, `open()` lance le rendu
       tout de suite : c'est précisément la personne pour qui les 80 Ko servent
       à quelque chose. C'est aussi la seule façon d'obtenir `roomThumb()`, donc
       la vignette de la carte « Votre pièce est prête ».

       Pour tous les autres - c'est-à-dire pour presque toute la page - rien
       n'est demandé à Render tant qu'un bouton n'a pas été survolé ou touché. */
    if (hasRoom()) vizLoad();
  }

  refreshPlacement();

  /* VAGUE 13 · L'ÉTAT DE « MA PIÈCE » EST LU TOUT DE SUITE, et c'est une
     exception assumée au « rien avant `load` » de la vague 12 - une exception
     que la règle elle-même autorise, parce qu'elle ne coûte rien de ce que la
     règle protège : aucun observateur, aucune animation, aucune requête. Une
     lecture de stockage local et une classe posée sur la racine.

     La faire attendre `load` coûterait, elle, quelque chose de visible : la
     carte afficherait « Voyez-le chez vous » et trente-six boutons diraient
     « Voir chez vous » pendant une seconde, avant de basculer sous les yeux du
     visiteur. Un texte juste vaut mieux qu'un texte qui se corrige. */
  if (CABINE) syncRoom();

  /* ★ VAGUE 16 : le mode traverse une navigation DANS la visite (retour depuis
     une fiche, page 2 de la collection). Il ne traverse pas la fermeture de
     l'onglet : `sessionStorage`, jamais `localStorage`. Et il ne se rallume
     jamais sans pièce mémorisée - sans elle, il n'y a rien à essayer. */
  if (CABINE && tryRemembered() === '1' && hasRoom()) {
    document.documentElement.classList.add(TRY_CLASS);
  }

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm, { once: true });

  /* Le rendu AJAX de `facets.js` recrée la grille : les blocs sont neufs, il
     faut les replacer et les ré-observer. La densité, elle, est posée sur
     `<html>` par `casemira-collection.js` : un observateur d'attribut suffit,
     et il évite d'aller poser un écouteur dans un fichier qui n'est pas à
     nous. */
  document.addEventListener('collection:rerendered', function () {
    window.setTimeout(arm, 0);
  });

  if (window.MutationObserver) {
    new MutationObserver(refreshPlacement).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-cmc-density', 'data-cmm-density'],
    });
  }

  var resizeTimer = 0;
  window.addEventListener(
    'resize',
    function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(refreshPlacement, 150);
    },
    { passive: true }
  );
})();
